import React, { useEffect, useState, useRef, Children, Fragment } from 'react';
import { motion } from 'framer-motion';
import { Star, Sun, Moon, CircleDot, ArrowUpRight, ShieldCheck, Zap, Heart, Briefcase, Activity, Compass, Sparkles, Calendar, MapPin, Clock, Download, BookOpen, Target, AlertTriangle, TrendingUp, Award, ChevronUp, FileText, Printer, X, Globe, Users } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { AdSlot } from '../components/ads/AdSlot';
import { PLANET_INTERPRETATIONS, ASCENDANT_INTERPRETATIONS } from '../data/natalInterpretations';
import { INTRO_CONTENT, EXTENDED_SUN, EXTENDED_MOON, EXTENDED_MERCURY, EXTENDED_VENUS, EXTENDED_MARS, EXTENDED_JUPITER, EXTENDED_SATURN, OUTER_PLANETS_EXTENDED, HOUSES_DETAILED, ASPECTS_DETAILED, HEALTH_SECTION, KARMIC_SECTION, TALENTS_SECTION, CHALLENGES_SECTION, FORECAST_SECTION, FINAL_ANALYSIS, PLANET_POSITIONS_TABLE, ENERGY_RADAR_DATA, ZODIAC_COMPATIBILITY, PERSONAL_AFFIRMATIONS, CURRENT_TRANSITS, CELEBRITY_MATCHES, NATAL_CHART_VISUAL } from '../data/fullReportContent';
interface FullNatalReportPageProps {
  onNavigate: (page: string) => void;
}
const TOC_ITEMS = [{
  id: 'section-01',
  title: 'Введение'
}, {
  id: 'section-02',
  title: 'Общий психологический портрет'
}, {
  id: 'section-03',
  title: 'Солнце — Ядро личности'
}, {
  id: 'section-04',
  title: 'Луна — Внутренний мир'
}, {
  id: 'section-05',
  title: 'Асцендент — Социальная маска'
}, {
  id: 'section-06',
  title: 'Меркурий — Мышление'
}, {
  id: 'section-07',
  title: 'Венера — Любовь и ценности'
}, {
  id: 'section-08',
  title: 'Марс — Энергия и действие'
}, {
  id: 'section-09',
  title: 'Юпитер — Расширение и удача'
}, {
  id: 'section-10',
  title: 'Сатурн — Дисциплина и уроки'
}, {
  id: 'section-11',
  title: 'Высшие планеты'
}, {
  id: 'section-12',
  title: 'Планеты в домах'
}, {
  id: 'section-13',
  title: 'Аспекты между планетами'
}, {
  id: 'section-14',
  title: 'Карьера и финансы'
}, {
  id: 'section-15',
  title: 'Любовь и отношения'
}, {
  id: 'section-16',
  title: 'Здоровье и энергетика'
}, {
  id: 'section-17',
  title: 'Кармические задачи'
}, {
  id: 'section-18',
  title: 'Таланты и предназначение'
}, {
  id: 'section-19',
  title: 'Жизненные вызовы'
}, {
  id: 'section-20',
  title: 'Прогноз жизненных периодов'
}, {
  id: 'section-21',
  title: 'Итоговый анализ'
}, {
  id: 'section-22',
  title: 'Шкала энергий'
}, {
  id: 'section-23',
  title: 'Совместимость по знакам'
}, {
  id: 'section-24',
  title: 'Персональные аффирмации'
}, {
  id: 'section-25',
  title: 'Транзиты текущего месяца'
}, {
  id: 'section-26',
  title: 'Знаменитости с похожей картой'
}, {
  id: 'section-27',
  title: 'Визуальная карта натала'
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.1,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  })
};
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = '',
  id
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return <motion.section id={id} className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-50px'
  }} variants={fadeUp} data-id="element-24581">
      {children}
    </motion.section>;
}
export function FullNatalReportPage({
  onNavigate
}: FullNatalReportPageProps) {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState<string>('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      // Calculate scroll progress
      const totalScroll = document.documentElement.scrollTop;
      const windowHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
      const scroll = `${totalScroll / windowHeight}`;
      setScrollProgress(Number(scroll) * 100);
      // Show/hide back to top button
      setShowBackToTop(totalScroll > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    // Set up intersection observer for the sticky TOC
    observerRef.current = new IntersectionObserver(entries => {
      // Find the section that is currently intersecting
      const visibleSections = entries.filter(entry => entry.isIntersecting);
      if (visibleSections.length > 0) {
        // If multiple are visible, pick the one closest to the top
        const topSection = visibleSections.reduce((prev, current) => {
          return prev.boundingClientRect.top > current.boundingClientRect.top ? prev : current;
        });
        setActiveSection(topSection.target.id);
      }
    }, {
      rootMargin: '-10% 0px -80% 0px',
      threshold: 0
    });
    // Observe all sections
    TOC_ITEMS.forEach(item => {
      const element = document.getElementById(item.id);
      if (element) observerRef.current?.observe(element);
    });
    return () => observerRef.current?.disconnect();
  }, []);
  // Track footer visibility to hide side nav
  useEffect(() => {
    const footerObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        // Once the dark summary block enters viewport, hide nav.
        // Use boundingClientRect to also hide when scrolled PAST it.
        if (entry.isIntersecting || entry.boundingClientRect.top < 0) {
          setFooterVisible(true);
        } else if (entry.boundingClientRect.top > window.innerHeight) {
          // Only show nav again if user scrolled back UP above the block
          setFooterVisible(false);
        }
      });
    }, {
      threshold: 0,
      rootMargin: '0px 0px 200px 0px'
    });
    if (footerRef.current) {
      footerObserver.observe(footerRef.current);
    }
    return () => footerObserver.disconnect();
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const SectionHeader = ({
    num,
    title,
    icon: Icon
  }: {
    num: string;
    title: string;
    icon?: any;
  }) => <div className="mb-12 relative overflow-hidden pl-4 md:pl-0 group" data-id="element-24582">
      <div className="absolute left-0 md:-left-8 top-1/2 -translate-y-1/2 text-6xl md:text-8xl font-black text-slate-100/60 -z-10 select-none transition-transform duration-500 group-hover:scale-110 group-hover:text-indigo-50/80" data-id="element-24583">
        {num}
      </div>
      <div className="flex items-center gap-4 pt-6 md:pt-8 relative z-10" data-id="element-24584">
        {Icon && <div className="p-2.5 bg-gradient-to-br from-indigo-50 to-white rounded-xl text-indigo-600 shadow-sm border border-indigo-100/50" data-id="element-24585">
            <Icon className="w-5 h-5 sm:w-6 sm:h-6" data-id="element-24586" />
          </div>}
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight" data-id="element-24587">
          {title}
        </h2>
      </div>
      <div className="h-px w-full bg-gradient-to-r from-indigo-200 via-indigo-100 to-transparent mt-5 relative z-10" data-id="element-24588"></div>
    </div>;
  const PullQuote = ({
    text
  }: {
    text: string;
  }) => <div className="my-10 p-6 md:p-8 bg-indigo-900 rounded-2xl text-white shadow-xl relative overflow-hidden" data-id="element-24589">
      <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500 rounded-full blur-3xl opacity-30 -translate-y-1/2 translate-x-1/2" data-id="element-24590"></div>
      <Sparkles className="w-8 h-8 text-indigo-300 mb-4 opacity-50" data-id="element-24591" />
      <p className="text-lg md:text-xl font-medium leading-relaxed relative z-10" data-id="element-24592">
        "{text}"
      </p>
    </div>;
  const AdviceBox = ({
    text
  }: {
    text: string;
  }) => <div className="mt-8 p-6 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-4" data-id="element-24593">
      <div className="p-2 bg-amber-100 rounded-full text-amber-600 flex-shrink-0" data-id="element-24594">
        <Star className="w-6 h-6" data-id="element-24595" />
      </div>
      <div data-id="element-24596">
        <h4 className="font-bold text-amber-900 mb-2 text-lg" data-id="element-24597">
          Совет астролога
        </h4>
        <p className="text-amber-800 text-base leading-relaxed" data-id="element-24598">{text}</p>
      </div>
    </div>;
  const ChapterDivider = () => <div className="flex items-center justify-center py-16 opacity-60" data-id="element-24599">
      <div className="h-px bg-slate-200 flex-1 max-w-[120px]" data-id="element-24600"></div>
      <Sparkles className="mx-6 text-indigo-300 w-5 h-5" data-id="element-24601" />
      <div className="h-px bg-slate-200 flex-1 max-w-[120px]" data-id="element-24602"></div>
    </div>;
  return <div className="min-h-screen bg-[#fafaf8] text-slate-800 font-sans selection:bg-indigo-100 selection:text-indigo-900 relative" data-id="element-24603">
      {/* PDF PREVIEW MODAL */}
      {showPdfPreview && <div className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 md:p-12" data-id="element-24604">
          <div className="bg-white w-full max-w-5xl h-full max-h-full rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-id="element-24605">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50" data-id="element-24606">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-24607">
                <FileText className="w-5 h-5 text-indigo-500" data-id="element-24608" />
                Предпросмотр PDF
              </h3>
              <div className="flex items-center gap-3" data-id="element-24609">
                <Button variant="outline" size="sm" onClick={() => window.print()} icon={<Printer className="w-4 h-4" data-id="element-24611" />} data-id="element-24610">
                  Печать
                </Button>
                <button onClick={() => setShowPdfPreview(false)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-24612">
                  <X className="w-5 h-5" data-id="element-24613" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-8 md:p-12 print:p-0 print:overflow-visible bg-slate-100" data-id="element-24614">
              <div className="max-w-3xl mx-auto bg-white shadow-sm border border-slate-200 p-6 sm:p-10 md:p-16 print:shadow-none print:border-none print:p-0" id="pdf-content" data-id="element-24615">
                {/* Print styles */}
                <style data-id="element-24616">{`
                  @media print {
                    body * { visibility: hidden; }
                    #pdf-content, #pdf-content * { visibility: visible; }
                    #pdf-content { position: absolute; left: 0; top: 0; width: 100%; }
                    @page { margin: 2cm; }
                    .print-break-before { page-break-before: always; }
                    .print-break-inside-avoid { page-break-inside: avoid; }
                  }
                `}</style>

                {/* Cover */}
                <div className="text-center mb-16 print-break-inside-avoid" data-id="element-24617">
                  <div className="text-sm font-bold text-indigo-600 uppercase tracking-widest mb-4" data-id="element-24618">
                    Astriqo • Персональный отчёт
                  </div>
                  <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-6" data-id="element-24619">
                    Натальная карта
                  </h1>
                  <div className="text-2xl font-bold text-slate-800 mb-8" data-id="element-24620">
                    Алексей Петров
                  </div>

                  <div className="flex flex-wrap justify-center gap-6 text-slate-600 mb-12" data-id="element-24621">
                    <span className="flex items-center gap-2" data-id="element-24622">
                      <Calendar className="w-4 h-4" data-id="element-24623" /> 15 августа 1992
                    </span>
                    <span className="flex items-center gap-2" data-id="element-24624">
                      <Clock className="w-4 h-4" data-id="element-24625" /> 14:30
                    </span>
                    <span className="flex items-center gap-2" data-id="element-24626">
                      <MapPin className="w-4 h-4" data-id="element-24627" /> Москва, Россия
                    </span>
                  </div>

                  <div className="w-48 h-48 mx-auto border-4 border-indigo-100 rounded-full flex items-center justify-center mb-8" data-id="element-24628">
                    <div className="text-6xl text-indigo-900" data-id="element-24629">♌</div>
                  </div>
                  <div className="text-lg font-bold text-indigo-900 uppercase tracking-widest" data-id="element-24630">
                    Лев
                  </div>
                </div>

                <div className="h-px w-full bg-slate-200 mb-16" data-id="element-24631"></div>

                {/* Big Three */}
                <div className="mb-16 print-break-inside-avoid" data-id="element-24632">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 border-b border-slate-200 pb-2" data-id="element-24633">
                    Ваша «Большая тройка»
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6" data-id="element-24634">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-24635">
                      <div className="text-xs font-bold text-slate-500 uppercase mb-1" data-id="element-24636">
                        Солнце (Эго)
                      </div>
                      <div className="text-xl font-bold text-slate-900" data-id="element-24637">
                        ♌ Лев
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-24638">
                      <div className="text-xs font-bold text-slate-500 uppercase mb-1" data-id="element-24639">
                        Луна (Душа)
                      </div>
                      <div className="text-xl font-bold text-slate-900" data-id="element-24640">
                        ♏ Скорпион
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-24641">
                      <div className="text-xs font-bold text-slate-500 uppercase mb-1" data-id="element-24642">
                        Асцендент (Маска)
                      </div>
                      <div className="text-xl font-bold text-slate-900" data-id="element-24643">
                        ♐ Стрелец
                      </div>
                    </div>
                  </div>
                  <p className="text-slate-700 leading-relaxed" data-id="element-24644">
                    Сочетание Льва, Скорпиона и Стрельца создает образ яркого,
                    страстного и целеустремленного человека. Внешне вы кажетесь
                    открытым и оптимистичным (Стрелец), ваше эго требует
                    признания и уважения (Лев), но внутри скрывается невероятно
                    глубокий, чувствительный и проницательный эмоциональный мир
                    (Скорпион), доступ к которому имеют лишь избранные.
                  </p>
                </div>

                {/* Section: Sun */}
                <div className="mb-12 print-break-before" data-id="element-24645">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24646">
                    ☉ Солнце во Льве — Ядро личности
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4" data-id="element-24647">
                    {PLANET_INTERPRETATIONS.sun[4].text}
                  </p>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24648">
                    Жизненная цель
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line" data-id="element-24649">
                    {EXTENDED_SUN.lifePurpose}
                  </p>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24650">
                    В повседневной жизни
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line" data-id="element-24651">
                    {EXTENDED_SUN.dailyLife}
                  </p>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24652">
                    В отношениях
                  </h3>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-24653">
                    {EXTENDED_SUN.inRelationships}
                  </p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 print-break-inside-avoid" data-id="element-24654">
                    <div className="bg-emerald-50 p-4 rounded-lg" data-id="element-24655">
                      <h4 className="font-bold text-emerald-800 mb-2 text-sm" data-id="element-24656">
                        Суперсилы
                      </h4>
                      <ul className="text-sm text-emerald-700 space-y-1" data-id="element-24657">
                        {PLANET_INTERPRETATIONS.sun[4].strengths.map((s, i) => <li key={i} data-id="element-24658">• {s}</li>)}
                      </ul>
                    </div>
                    <div className="bg-rose-50 p-4 rounded-lg" data-id="element-24659">
                      <h4 className="font-bold text-rose-800 mb-2 text-sm" data-id="element-24660">
                        Теневая сторона
                      </h4>
                      <ul className="text-sm text-rose-700 space-y-1" data-id="element-24661">
                        {PLANET_INTERPRETATIONS.sun[4].challenges.map((c, i) => <li key={i} data-id="element-24662">• {c}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Section: Moon */}
                <div className="mb-12 print-break-before" data-id="element-24663">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24664">
                    ☽ Луна в Скорпионе — Внутренний мир
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4" data-id="element-24665">
                    {PLANET_INTERPRETATIONS.moon[7].text}
                  </p>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24666">
                    Эмоциональные паттерны
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line" data-id="element-24667">
                    {EXTENDED_MOON.emotionalPatterns}
                  </p>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24668">
                    Скрытые потребности
                  </h3>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-24669">
                    {EXTENDED_MOON.hiddenNeeds}
                  </p>
                </div>

                {/* Section: Ascendant */}
                <div className="mb-12 print-break-before" data-id="element-24670">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24671">
                    Асцендент в Стрельце — Социальная маска
                  </h2>
                  <p className="text-slate-700 leading-relaxed" data-id="element-24672">
                    {ASCENDANT_INTERPRETATIONS[8].text}
                  </p>
                </div>

                {/* Section: Mercury */}
                <div className="mb-12 print-break-inside-avoid" data-id="element-24673">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24674">
                    ☿ Меркурий во Льве — Мышление
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4" data-id="element-24675">
                    {PLANET_INTERPRETATIONS.mercury[4].text}
                  </p>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-24676">
                    {EXTENDED_MERCURY.communicationStyle}
                  </p>
                </div>

                {/* Section: Venus */}
                <div className="mb-12 print-break-inside-avoid" data-id="element-24677">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24678">
                    ♀ Венера в Раке — Любовь и ценности
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4" data-id="element-24679">
                    {PLANET_INTERPRETATIONS.venus[3].text}
                  </p>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-24680">
                    {EXTENDED_VENUS.loveLanguage}
                  </p>
                </div>

                {/* Section: Mars */}
                <div className="mb-12 print-break-inside-avoid" data-id="element-24681">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24682">
                    ♂ Марс в Близнецах — Энергия и действие
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4" data-id="element-24683">
                    {PLANET_INTERPRETATIONS.mars[2].text}
                  </p>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-24684">
                    {EXTENDED_MARS.ambitionStyle}
                  </p>
                </div>

                {/* Section: Jupiter & Saturn */}
                <div className="mb-12 print-break-before" data-id="element-24685">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24686">
                    ♃ Юпитер в Деве / ♄ Сатурн в Водолее
                  </h2>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24687">
                    Юпитер в Деве (10 дом)
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-6 whitespace-pre-line" data-id="element-24688">
                    {EXTENDED_JUPITER}
                  </p>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24689">
                    Сатурн в Водолее (3 дом)
                  </h3>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-24690">
                    {EXTENDED_SATURN}
                  </p>
                </div>

                {/* Section: Outer Planets */}
                <div className="mb-12 print-break-before" data-id="element-24691">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24692">
                    Высшие планеты
                  </h2>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24693">
                    ♅ Уран в Козероге
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line" data-id="element-24694">
                    {OUTER_PLANETS_EXTENDED.uranus}
                  </p>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24695">
                    ♆ Нептун в Козероге
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line" data-id="element-24696">
                    {OUTER_PLANETS_EXTENDED.neptune}
                  </p>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24697">
                    ♇ Плутон в Скорпионе
                  </h3>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-24698">
                    {OUTER_PLANETS_EXTENDED.pluto}
                  </p>
                </div>

                {/* Section: Houses */}
                <div className="mb-12 print-break-before" data-id="element-24699">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24700">
                    Планеты в домах
                  </h2>
                  <div className="space-y-4" data-id="element-24701">
                    {HOUSES_DETAILED.map(house => <div key={house.num} className="print-break-inside-avoid" data-id="element-24702">
                        <h3 className="text-base font-bold text-slate-800 mb-1" data-id="element-24703">
                          {house.num} дом — {house.theme} ({house.sign})
                          {house.planets.length > 0 && <span className="text-indigo-600 font-normal ml-2" data-id="element-24704">
                              Планеты: {house.planets.join(', ')}
                            </span>}
                        </h3>
                        <p className="text-slate-700 text-sm leading-relaxed" data-id="element-24705">
                          {house.text}
                        </p>
                      </div>)}
                  </div>
                </div>

                {/* Section: Aspects */}
                <div className="mb-12 print-break-before" data-id="element-24706">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24707">
                    Аспекты между планетами
                  </h2>
                  <div className="space-y-4" data-id="element-24708">
                    {ASPECTS_DETAILED.map((aspect, idx) => <div key={idx} className="print-break-inside-avoid" data-id="element-24709">
                        <h3 className="text-base font-bold text-slate-800 mb-1" data-id="element-24710">
                          {aspect.title}
                          <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${aspect.nature === 'harmonious' ? 'bg-emerald-100 text-emerald-700' : aspect.nature === 'tense' ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}`} data-id="element-24711">
                            {aspect.nature === 'harmonious' ? 'Гармоничный' : aspect.nature === 'tense' ? 'Напряжённый' : 'Интенсивный'}
                          </span>
                        </h3>
                        <p className="text-slate-700 text-sm leading-relaxed" data-id="element-24712">
                          {aspect.text}
                        </p>
                      </div>)}
                  </div>
                </div>

                {/* Section: Career */}
                <div className="mb-12 print-break-before" data-id="element-24713">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24714">
                    Карьера и финансы
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4" data-id="element-24715">
                    С Юпитером в 10-м доме в Деве ваш социальный успех приходит
                    через экспертность, аналитику и служение. Ваш авторитет
                    строится на высочайшем профессионализме и внимании к
                    деталям.
                  </p>
                  <p className="text-slate-700 leading-relaxed mb-4" data-id="element-24716">
                    Идеальные сферы: IT-архитектура, аналитика данных, медицина,
                    юриспруденция, преподавание, управление сложными системами.
                  </p>
                  <p className="text-slate-700 leading-relaxed" data-id="element-24717">
                    Второй дом в Козероге говорит о постепенном, стабильном
                    финансовом росте. Уран и Нептун во 2-м доме могут давать
                    нестандартные источники дохода через технологии или
                    эзотерику.
                  </p>
                </div>

                {/* Section: Love */}
                <div className="mb-12 print-break-inside-avoid" data-id="element-24718">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24719">
                    Любовь и отношения
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4" data-id="element-24720">
                    7-й дом в Близнецах требует лёгкости и интеллектуального
                    общения, а Венера в Раке и Луна в Скорпионе жаждут
                    глубочайшей эмоциональной привязанности. Вам нужен партнёр,
                    сочетающий ум с эмоциональной глубиной.
                  </p>
                  <p className="text-slate-700 leading-relaxed" data-id="element-24721">
                    Главный конфликт — борьба между потребностью в независимости
                    и сильным собственническим инстинктом. Задача — научиться
                    доверять и не контролировать партнёра.
                  </p>
                </div>

                {/* Section: Health */}
                <div className="mb-12 print-break-before" data-id="element-24722">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24723">
                    Здоровье и энергетика
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4" data-id="element-24724">
                    {HEALTH_SECTION.overview}
                  </p>
                  <div className="bg-slate-50 p-4 rounded-lg" data-id="element-24725">
                    <h3 className="font-bold text-slate-800 mb-2 text-sm" data-id="element-24726">
                      Рекомендации
                    </h3>
                    <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line" data-id="element-24727">
                      {HEALTH_SECTION.recommendations}
                    </p>
                  </div>
                </div>

                {/* Section: Karmic */}
                <div className="mb-12 print-break-inside-avoid" data-id="element-24728">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24729">
                    Кармические задачи
                  </h2>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24730">
                    Северный узел (Куда идти)
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line" data-id="element-24731">
                    {KARMIC_SECTION.northNode}
                  </p>
                  <h3 className="text-lg font-bold text-slate-800 mb-2" data-id="element-24732">
                    Южный узел (Что оставить)
                  </h3>
                  <p className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line" data-id="element-24733">
                    {KARMIC_SECTION.southNode}
                  </p>
                  <div className="bg-indigo-50 p-4 rounded-lg mt-4" data-id="element-24734">
                    <p className="text-indigo-800 font-medium italic" data-id="element-24735">
                      «{KARMIC_SECTION.lifeMission}»
                    </p>
                  </div>
                </div>

                {/* Section: Talents */}
                <div className="mb-12 print-break-before" data-id="element-24736">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24737">
                    Таланты и предназначение
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-4 whitespace-pre-line" data-id="element-24738">
                    {TALENTS_SECTION.naturalAbilities}
                  </p>
                  <div className="bg-slate-50 p-4 rounded-lg" data-id="element-24739">
                    <h3 className="font-bold text-slate-800 mb-2 text-sm" data-id="element-24740">
                      Лучшие сферы реализации
                    </h3>
                    <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line" data-id="element-24741">
                      {TALENTS_SECTION.bestAreas}
                    </p>
                  </div>
                </div>

                {/* Section: Challenges */}
                <div className="mb-12 print-break-inside-avoid" data-id="element-24742">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24743">
                    Жизненные вызовы
                  </h2>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-id="element-24744">
                    {CHALLENGES_SECTION.mainChallenges}
                  </p>
                </div>

                {/* Section: Forecast */}
                <div className="mb-12 print-break-before" data-id="element-24745">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24746">
                    Прогноз жизненных периодов
                  </h2>
                  <div className="space-y-4" data-id="element-24747">
                    {FORECAST_SECTION.periods.map((period, idx) => <div key={idx} className="print-break-inside-avoid" data-id="element-24748">
                        <h3 className="text-base font-bold text-slate-800 mb-1" data-id="element-24749">
                          <span className="text-indigo-600" data-id="element-24750">{period.age}</span>{' '}
                          — {period.title}
                        </h3>
                        <p className="text-slate-700 text-sm leading-relaxed" data-id="element-24751">
                          {period.text}
                        </p>
                      </div>)}
                  </div>
                </div>

                {/* Section: Final Analysis */}
                <div className="mb-12 print-break-before" data-id="element-24752">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4 border-b border-slate-200 pb-2" data-id="element-24753">
                    Итоговый анализ
                  </h2>
                  <p className="text-slate-700 leading-relaxed mb-6 whitespace-pre-line" data-id="element-24754">
                    {FINAL_ANALYSIS.summary}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 print-break-inside-avoid" data-id="element-24755">
                    <div className="bg-slate-50 p-4 rounded-lg" data-id="element-24756">
                      <h3 className="font-bold text-slate-800 mb-3 text-sm" data-id="element-24757">
                        Ключевые качества
                      </h3>
                      <ul className="text-sm text-slate-700 space-y-1" data-id="element-24758">
                        {FINAL_ANALYSIS.keyQualities.map((q, i) => <li key={i} data-id="element-24759">• {q}</li>)}
                      </ul>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg" data-id="element-24760">
                      <h3 className="font-bold text-slate-800 mb-3 text-sm" data-id="element-24761">
                        Главные темы жизни
                      </h3>
                      <ul className="text-sm text-slate-700 space-y-1" data-id="element-24762">
                        {FINAL_ANALYSIS.lifeThemes.map((t, i) => <li key={i} data-id="element-24763">• {t}</li>)}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-indigo-50 p-4 rounded-lg print-break-inside-avoid" data-id="element-24764">
                    <p className="text-indigo-800 font-medium italic leading-relaxed" data-id="element-24765">
                      «{FINAL_ANALYSIS.finalAdvice}»
                    </p>
                  </div>
                </div>

                {/* Planet Positions */}
                <div className="mb-16 print-break-before" data-id="element-24766">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 border-b border-slate-200 pb-2" data-id="element-24767">
                    Приложение: Положения планет
                  </h2>
                  <table className="w-full text-left text-sm" data-id="element-24768">
                    <thead className="bg-slate-100 text-slate-600" data-id="element-24769">
                      <tr data-id="element-24770">
                        <th className="px-4 py-3 font-bold" data-id="element-24771">Планета</th>
                        <th className="px-4 py-3 font-bold" data-id="element-24772">Знак</th>
                        <th className="px-4 py-3 font-bold" data-id="element-24773">Градус</th>
                        <th className="px-4 py-3 font-bold" data-id="element-24774">Дом</th>
                        <th className="px-4 py-3 font-bold" data-id="element-24775">Достоинство</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200" data-id="element-24776">
                      {PLANET_POSITIONS_TABLE.map((row, i) => <tr key={i} data-id="element-24777">
                          <td className="px-4 py-3 font-medium text-slate-900" data-id="element-24778">
                            {row.symbol} {row.planet}
                          </td>
                          <td className="px-4 py-3 text-slate-700" data-id="element-24779">
                            {row.sign}
                          </td>
                          <td className="px-4 py-3 text-slate-500 font-mono" data-id="element-24780">
                            {row.degree}
                          </td>
                          <td className="px-4 py-3 text-slate-700 font-bold" data-id="element-24781">
                            {row.house}
                          </td>
                          <td className="px-4 py-3 text-slate-500" data-id="element-24782">
                            {row.dignity}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>

                {/* Disclaimer */}
                <div className="mt-24 pt-8 border-t border-slate-200 text-sm text-slate-500 text-center print-break-inside-avoid" data-id="element-24783">
                  <p className="mb-2" data-id="element-24784">
                    Сгенерировано платформой Astriqo •{' '}
                    {new Date().toLocaleDateString('ru-RU')}
                  </p>
                  <p data-id="element-24785">
                    Астрология является инструментом самопознания. Данный отчет
                    носит рекомендательный характер и не заменяет консультации
                    профильных специалистов.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* 1. READING PROGRESS BAR */}
      <div className="fixed top-0 left-0 w-full h-1 bg-slate-100 z-[60]" data-id="element-24786">
        <div className="h-full bg-gradient-to-r from-indigo-400 to-indigo-600 transition-all duration-150 ease-out" style={{
        width: `${scrollProgress}%`
      }} data-id="element-24787" />
      </div>

      {/* 2. FLOATING MINI-NAVIGATION (Sticky TOC) */}
      <div className={`hidden xl:flex flex-col gap-1 fixed left-8 top-1/2 -translate-y-1/2 z-40 transition-all duration-300 max-h-[85vh] overflow-y-auto scrollbar-none py-2 ${footerVisible ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} style={{
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }} data-id="element-24788">
        {TOC_ITEMS.map((item, index) => {
        const isActive = activeSection === item.id;
        const num = String(index + 1).padStart(2, '0');
        return <div key={item.id} className="relative group flex items-center justify-center w-5 h-5 flex-shrink-0" data-id="element-24789">
              <button onClick={() => scrollToSection(item.id)} className={`rounded-full flex items-center justify-center text-[8px] font-bold transition-all duration-300 ${isActive ? 'w-5 h-5 bg-indigo-600 text-white shadow-md scale-110' : 'w-1.5 h-1.5 bg-slate-300 text-transparent hover:w-5 hover:h-5 hover:bg-indigo-100 hover:text-indigo-600'}`} aria-label={item.title} data-id="element-24790">
                {isActive ? num : <span className="opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-24791">
                    {num}
                  </span>}
              </button>

              {/* Tooltip */}
              <div className="absolute left-full ml-3 px-3 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none shadow-lg" data-id="element-24792">
                {item.title}
                <div className="absolute top-1/2 -left-1 -translate-y-1/2 border-4 border-transparent border-r-slate-800" data-id="element-24793" />
              </div>
            </div>;
      })}
      </div>

      {/* 3. BACK TO TOP BUTTON */}
      <button onClick={scrollToTop} className={`fixed bottom-8 right-8 z-50 p-3 bg-indigo-600 text-white rounded-full shadow-xl hover:bg-indigo-700 hover:scale-110 transition-all duration-300 ${showBackToTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`} aria-label="Наверх" data-id="element-24794">
        <ChevronUp className="w-6 h-6" data-id="element-24795" />
      </button>

      {/* COVER SECTION */}
      <header className="relative overflow-hidden bg-gradient-to-br from-indigo-950 via-slate-900 to-indigo-900 pt-24 sm:pt-28 pb-20 sm:pb-28" data-id="element-24796">
        {/* Animated constellation background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-24797">
          <div className="absolute top-[10%] left-[15%] w-1 h-1 bg-white/30 rounded-full animate-pulse" data-id="element-24798" />
          <div className="absolute top-[25%] right-[20%] w-1.5 h-1.5 bg-indigo-300/40 rounded-full animate-pulse" style={{
          animationDelay: '0.5s'
        }} data-id="element-24799" />
          <div className="absolute top-[60%] left-[8%] w-1 h-1 bg-white/20 rounded-full animate-pulse" style={{
          animationDelay: '1s'
        }} data-id="element-24800" />
          <div className="absolute top-[15%] right-[35%] w-1 h-1 bg-indigo-200/30 rounded-full animate-pulse" style={{
          animationDelay: '1.5s'
        }} data-id="element-24801" />
          <div className="absolute bottom-[30%] right-[12%] w-1 h-1 bg-white/25 rounded-full animate-pulse" style={{
          animationDelay: '2s'
        }} data-id="element-24802" />
          <div className="absolute top-[45%] left-[40%] w-0.5 h-0.5 bg-white/20 rounded-full animate-pulse" style={{
          animationDelay: '0.8s'
        }} data-id="element-24803" />
          <div className="absolute bottom-[20%] left-[25%] w-1 h-1 bg-indigo-300/20 rounded-full animate-pulse" style={{
          animationDelay: '1.2s'
        }} data-id="element-24804" />
          <div className="absolute -top-[20%] -right-[10%] w-[60%] h-[80%] rounded-full bg-indigo-500/5 blur-[100px]" data-id="element-24805" />
          <div className="absolute bottom-[0%] -left-[15%] w-[50%] h-[70%] rounded-full bg-purple-500/5 blur-[100px]" data-id="element-24806" />
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-24807">
          <motion.div className="text-center mb-10 sm:mb-14" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} data-id="element-24808">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 text-indigo-200 text-sm font-medium mb-8" data-id="element-24809">
              <Sparkles className="w-3.5 h-3.5" data-id="element-24810" />
              Персональный отчёт • Astriqo
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-extrabold text-white mb-5 sm:mb-6 tracking-tight leading-[1.1]" data-id="element-24811">
              Натальная карта
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-indigo-200/80 font-medium max-w-2xl mx-auto leading-relaxed" data-id="element-24812">
              Полный астрологический разбор личности, талантов и жизненного пути
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.3,
          ease: [0.25, 0.46, 0.45, 0.94]
        }} data-id="element-24813">
            <Card padding="lg" className="max-w-2xl mx-auto bg-white/[0.07] backdrop-blur-xl shadow-2xl shadow-black/20 border-white/10 rounded-3xl relative overflow-hidden" data-id="element-24814">
              {/* Decorative chart background */}
              <div className="absolute -right-16 -top-16 w-64 h-64 opacity-[0.06] pointer-events-none" data-id="element-24815">
                <svg viewBox="0 0 100 100" className="w-full h-full animate-[spin_120s_linear_infinite]" data-id="element-24816">
                  <circle cx="50" cy="50" r="48" fill="none" stroke="white" strokeWidth="1" data-id="element-24817" />
                  <circle cx="50" cy="50" r="38" fill="none" stroke="white" strokeWidth="0.5" data-id="element-24818" />
                  <circle cx="50" cy="50" r="28" fill="none" stroke="white" strokeWidth="0.5" strokeDasharray="2 2" data-id="element-24819" />
                  {[...Array(12)].map((_, i) => <line key={i} x1="50" y1="12" x2="50" y2="2" transform={`rotate(${i * 30} 50 50)`} stroke="white" strokeWidth="1" data-id="element-24820" />)}
                  {[...Array(12)].map((_, i) => <line key={`inner-${i}`} x1="50" y1="38" x2="50" y2="28" transform={`rotate(${i * 30 + 15} 50 50)`} stroke="white" strokeWidth="0.5" data-id="element-24821" />)}
                </svg>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative z-10" data-id="element-24822">
                <div className="space-y-5 flex-1 w-full" data-id="element-24823">
                  <div data-id="element-24824">
                    <div className="text-xs font-bold text-indigo-300 uppercase tracking-[0.2em] mb-2" data-id="element-24825">
                      Владелец карты
                    </div>
                    <div className="text-3xl font-bold text-white" data-id="element-24826">
                      Алексей Петров
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-5 bg-white/[0.06] backdrop-blur-sm p-4 sm:p-5 rounded-2xl border border-white/10" data-id="element-24827">
                    <div className="flex items-center gap-3 text-indigo-100 font-medium text-sm" data-id="element-24828">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-indigo-300" data-id="element-24829">
                        <Calendar className="w-4 h-4" data-id="element-24830" />
                      </div>
                      <span data-id="element-24831">15 августа 1992</span>
                    </div>
                    <div className="flex items-center gap-3 text-indigo-100 font-medium text-sm" data-id="element-24832">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-indigo-300" data-id="element-24833">
                        <Clock className="w-4 h-4" data-id="element-24834" />
                      </div>
                      <span data-id="element-24835">14:30</span>
                    </div>
                    <div className="flex items-center gap-3 text-indigo-100 font-medium text-sm sm:col-span-2" data-id="element-24836">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-indigo-300" data-id="element-24837">
                        <MapPin className="w-4 h-4" data-id="element-24838" />
                      </div>
                      <span data-id="element-24839">Москва, Россия</span>
                    </div>
                  </div>
                </div>

                <div className="w-28 h-28 md:w-36 md:h-36 relative flex-shrink-0 flex items-center justify-center mx-auto md:mx-0" data-id="element-24840">
                  <svg viewBox="0 0 100 100" className="w-full h-full animate-[spin_60s_linear_infinite] opacity-30" data-id="element-24841">
                    <circle cx="50" cy="50" r="48" fill="none" stroke="white" strokeWidth="0.5" strokeDasharray="2 2" data-id="element-24842" />
                    <circle cx="50" cy="50" r="38" fill="none" stroke="white" strokeWidth="0.5" data-id="element-24843" />
                    {[...Array(12)].map((_, i) => <line key={i} x1="50" y1="12" x2="50" y2="2" transform={`rotate(${i * 30} 50 50)`} stroke="white" strokeWidth="0.5" data-id="element-24844" />)}
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-center" data-id="element-24845">
                    <motion.div animate={{
                    rotate: [0, 5, -5, 0]
                  }} transition={{
                    duration: 6,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-24846">
                      <div className="text-4xl text-white/90" data-id="element-24847">♌</div>
                      <div className="text-[10px] font-bold text-indigo-300 uppercase tracking-[0.2em] mt-1.5" data-id="element-24848">
                        Лев
                      </div>
                    </motion.div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-4 relative z-10" data-id="element-24849">
                <div className="text-sm text-indigo-300/70 font-medium" data-id="element-24850">
                  Объем: ~60 страниц • Сгенерировано:{' '}
                  {new Date().toLocaleDateString('ru-RU')}
                </div>
                <Button variant="primary" size="md" icon={<Download className="w-4 h-4" data-id="element-24852" />} className="w-full sm:w-auto group relative overflow-hidden" onClick={() => setShowPdfPreview(true)} data-id="element-24851">
                  <span className="relative z-10" data-id="element-24853">Скачать PDF</span>
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-24854"></div>
                </Button>
              </div>
            </Card>
          </motion.div>

          {/* Big Three mini badges */}
          <motion.div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 mt-10 sm:mt-14" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} data-id="element-24855">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.07] backdrop-blur-sm border border-white/10 text-sm" data-id="element-24856">
              <span className="text-amber-400 text-lg" data-id="element-24857">☉</span>
              <span className="text-white/80 font-medium" data-id="element-24858">Лев</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.07] backdrop-blur-sm border border-white/10 text-sm" data-id="element-24859">
              <span className="text-blue-300 text-lg" data-id="element-24860">☽</span>
              <span className="text-white/80 font-medium" data-id="element-24861">Скорпион</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.07] backdrop-blur-sm border border-white/10 text-sm" data-id="element-24862">
              <span className="text-purple-300 text-lg" data-id="element-24863">↑</span>
              <span className="text-white/80 font-medium" data-id="element-24864">Стрелец</span>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-id="element-24865">
        {/* TABLE OF CONTENTS */}
        <motion.div className="mb-16 sm:mb-24 bg-white p-5 sm:p-8 md:p-12 rounded-2xl sm:rounded-3xl shadow-sm border border-slate-200" initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} data-id="element-24866">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 sm:mb-8 flex items-center gap-3" data-id="element-24867">
            <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-500" data-id="element-24868" />
            Содержание отчёта
          </h3>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-4" variants={staggerContainer} data-id="element-24869">
            {TOC_ITEMS.map((item, index) => <motion.button key={item.id} variants={staggerItem} onClick={() => scrollToSection(item.id)} className="flex items-center text-left group py-1.5" data-id="element-24870">
                <span className="text-indigo-300 font-mono text-sm mr-4 w-6" data-id="element-24871">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="text-slate-600 group-hover:text-indigo-600 font-medium transition-colors border-b border-transparent group-hover:border-indigo-200 pb-0.5 text-sm sm:text-base" data-id="element-24872">
                  {item.title}
                </span>
              </motion.button>)}
          </motion.div>
        </motion.div>

        <div data-id="element-24873">
          {/* SECTION 01: ВВЕДЕНИЕ */}
          <AnimatedSection id="section-01" className="scroll-mt-24" data-id="element-24874">
            <SectionHeader num="01" title={INTRO_CONTENT.title} icon={Compass} data-id="element-24875" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-24876">
              <p className="lead text-base sm:text-xl leading-relaxed text-slate-700 mb-8" data-id="element-24877">
                {INTRO_CONTENT.whatIsNatalChart}
              </p>
              <h4 className="text-2xl font-bold mt-10 mb-4" data-id="element-24878">
                Как работает астрология
              </h4>
              <p className="whitespace-pre-line" data-id="element-24879">{INTRO_CONTENT.howItWorks}</p>
              <h4 className="text-2xl font-bold mt-10 mb-4" data-id="element-24880">
                Как правильно читать этот отчёт
              </h4>
              <p className="whitespace-pre-line" data-id="element-24881">{INTRO_CONTENT.howToRead}</p>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-24882" />

          {/* SECTION 02: ОБЩИЙ ПОРТРЕТ */}
          <AnimatedSection id="section-02" className="scroll-mt-24" data-id="element-24883">
            <SectionHeader num="02" title="Общий психологический портрет" icon={Sparkles} data-id="element-24884" />

            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-12" data-id="element-24885">
              <p className="text-base sm:text-xl leading-relaxed text-slate-700" data-id="element-24886">
                Алексей, ваша натальная карта представляет собой уникальный узор
                энергий. Вы — человек с мощной{' '}
                <strong data-id="element-24887">фиксированной энергией</strong>, что даёт вам
                невероятную стойкость, упорство и глубину. Преобладание водной и
                земной стихий говорит о сочетании эмоциональной глубины с
                практичностью. Вы не просто мечтаете, вы способны
                материализовать свои глубокие внутренние импульсы.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-24888">
              <Card padding="lg" className="bg-white shadow-md border-slate-200" data-id="element-24889">
                <h4 className="font-bold text-xl text-slate-900 mb-8" data-id="element-24890">
                  Баланс стихий
                </h4>
                <div className="space-y-6" data-id="element-24891">
                  <div data-id="element-24892">
                    <div className="flex justify-between text-base mb-2" data-id="element-24893">
                      <span className="font-medium text-slate-700" data-id="element-24894">
                        Огонь (20%)
                      </span>
                      <span className="text-slate-400 text-sm" data-id="element-24895">
                        Солнце, Меркурий
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-24896">
                      <motion.div className="bg-red-500 h-3 rounded-full" initial={{
                      width: 0
                    }} whileInView={{
                      width: '20%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      ease: 'easeOut'
                    }} data-id="element-24897"></motion.div>
                    </div>
                  </div>
                  <div data-id="element-24898">
                    <div className="flex justify-between text-base mb-2" data-id="element-24899">
                      <span className="font-medium text-slate-700" data-id="element-24900">
                        Земля (30%)
                      </span>
                      <span className="text-slate-400 text-sm" data-id="element-24901">
                        Юпитер, Уран, Нептун
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-24902">
                      <motion.div className="bg-emerald-500 h-3 rounded-full" initial={{
                      width: 0
                    }} whileInView={{
                      width: '30%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      ease: 'easeOut',
                      delay: 0.1
                    }} data-id="element-24903"></motion.div>
                    </div>
                  </div>
                  <div data-id="element-24904">
                    <div className="flex justify-between text-base mb-2" data-id="element-24905">
                      <span className="font-medium text-slate-700" data-id="element-24906">
                        Воздух (20%)
                      </span>
                      <span className="text-slate-400 text-sm" data-id="element-24907">
                        Марс, Сатурн
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-24908">
                      <motion.div className="bg-blue-500 h-3 rounded-full" initial={{
                      width: 0
                    }} whileInView={{
                      width: '20%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      ease: 'easeOut',
                      delay: 0.2
                    }} data-id="element-24909"></motion.div>
                    </div>
                  </div>
                  <div data-id="element-24910">
                    <div className="flex justify-between text-base mb-2" data-id="element-24911">
                      <span className="font-bold text-indigo-700" data-id="element-24912">
                        Вода (30%) — Доминанта
                      </span>
                      <span className="text-slate-400 text-sm" data-id="element-24913">
                        Луна, Венера, Плутон
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-24914">
                      <motion.div className="bg-indigo-500 h-3 rounded-full" initial={{
                      width: 0
                    }} whileInView={{
                      width: '30%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      ease: 'easeOut',
                      delay: 0.3
                    }} data-id="element-24915"></motion.div>
                    </div>
                  </div>
                </div>
              </Card>

              <Card padding="lg" className="bg-white shadow-md border-slate-200" data-id="element-24916">
                <h4 className="font-bold text-xl text-slate-900 mb-8" data-id="element-24917">
                  Баланс крестов (модальностей)
                </h4>
                <div className="space-y-6" data-id="element-24918">
                  <div data-id="element-24919">
                    <div className="flex justify-between text-base mb-2" data-id="element-24920">
                      <span className="font-medium text-slate-700" data-id="element-24921">
                        Кардинальный (30%)
                      </span>
                      <span className="text-slate-400 text-sm" data-id="element-24922">Инициатива</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-24923">
                      <motion.div className="bg-slate-400 h-3 rounded-full" initial={{
                      width: 0
                    }} whileInView={{
                      width: '30%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      ease: 'easeOut'
                    }} data-id="element-24924"></motion.div>
                    </div>
                  </div>
                  <div data-id="element-24925">
                    <div className="flex justify-between text-base mb-2" data-id="element-24926">
                      <span className="font-bold text-amber-600" data-id="element-24927">
                        Фиксированный (50%) — Доминанта
                      </span>
                      <span className="text-slate-400 text-sm" data-id="element-24928">
                        Стабильность
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-24929">
                      <motion.div className="bg-amber-500 h-3 rounded-full" initial={{
                      width: 0
                    }} whileInView={{
                      width: '50%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      ease: 'easeOut',
                      delay: 0.1
                    }} data-id="element-24930"></motion.div>
                    </div>
                  </div>
                  <div data-id="element-24931">
                    <div className="flex justify-between text-base mb-2" data-id="element-24932">
                      <span className="font-medium text-slate-700" data-id="element-24933">
                        Мутабельный (20%)
                      </span>
                      <span className="text-slate-400 text-sm" data-id="element-24934">Адаптация</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-24935">
                      <motion.div className="bg-slate-300 h-3 rounded-full" initial={{
                      width: 0
                    }} whileInView={{
                      width: '20%'
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      ease: 'easeOut',
                      delay: 0.2
                    }} data-id="element-24936"></motion.div>
                    </div>
                  </div>
                </div>
                <p className="text-base text-slate-600 mt-8 leading-relaxed" data-id="element-24937">
                  Доминирование фиксированного креста делает вас человеком,
                  который неохотно меняет свои взгляды, но обладает колоссальной
                  пробивной силой. Вы способны удерживать фокус на цели годами.
                </p>
              </Card>
            </div>

            <div className="bg-indigo-50/50 border border-indigo-100 rounded-3xl p-8 md:p-10" data-id="element-24938">
              <h4 className="font-bold text-indigo-900 mb-6 text-2xl" data-id="element-24939">
                Ваша «Большая тройка»
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-24940">
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-indigo-50" data-id="element-24941">
                  <div className="text-sm font-bold text-slate-400 uppercase mb-2 tracking-wider" data-id="element-24942">
                    Солнце (Эго)
                  </div>
                  <div className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-id="element-24943">
                    <span className="text-amber-500 text-3xl" data-id="element-24944">♌</span> Лев
                  </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-indigo-50" data-id="element-24945">
                  <div className="text-sm font-bold text-slate-400 uppercase mb-2 tracking-wider" data-id="element-24946">
                    Луна (Душа)
                  </div>
                  <div className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-id="element-24947">
                    <span className="text-indigo-500 text-3xl" data-id="element-24948">♏</span>{' '}
                    Скорпион
                  </div>
                </div>
                <div className="bg-white p-6 rounded-2xl shadow-sm border border-indigo-50" data-id="element-24949">
                  <div className="text-sm font-bold text-slate-400 uppercase mb-2 tracking-wider" data-id="element-24950">
                    Асцендент (Маска)
                  </div>
                  <div className="text-2xl font-bold text-slate-900 flex items-center gap-3" data-id="element-24951">
                    <span className="text-purple-500 text-3xl" data-id="element-24952">♐</span> Стрелец
                  </div>
                </div>
              </div>
              <p className="mt-8 text-slate-700 text-lg leading-relaxed" data-id="element-24953">
                Сочетание Льва, Скорпиона и Стрельца создает образ яркого,
                страстного и целеустремленного человека. Внешне вы кажетесь
                открытым и оптимистичным (Стрелец), ваше эго требует признания и
                уважения (Лев), но внутри скрывается невероятно глубокий,
                чувствительный и проницательный эмоциональный мир (Скорпион),
                доступ к которому имеют лишь избранные.
              </p>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-24954" />

          {/* SECTION 03: SUN */}
          <AnimatedSection id="section-03" className="scroll-mt-24" data-id="element-24955">
            <SectionHeader num="03" title="Солнце — Ядро личности" icon={Sun} data-id="element-24956" />

            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-24957">
              <div className="flex items-center gap-5 mb-8 p-6 bg-amber-50 rounded-2xl border border-amber-100" data-id="element-24958">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl text-amber-500 shadow-sm" data-id="element-24959">
                  ♌
                </div>
                <div data-id="element-24960">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-24961">
                    Солнце во Льве
                  </h3>
                  <p className="text-amber-600 font-medium text-lg m-0 mt-1" data-id="element-24962">
                    Обитель Солнца • Огненная стихия
                  </p>
                </div>
              </div>

              <p className="lead text-base sm:text-xl" data-id="element-24963">
                {PLANET_INTERPRETATIONS.sun[4].text}
              </p>

              <h4 className="text-lg sm:text-xl font-bold mt-8 mb-3" data-id="element-24964">
                Ваша жизненная цель
              </h4>
              <p className="whitespace-pre-line" data-id="element-24965">{EXTENDED_SUN.lifePurpose}</p>

              <PullQuote text="Ваша главная жизненная потребность — быть замеченным, оцененным по достоинству и любимым за то, кем вы являетесь на самом деле." data-id="element-24966" />

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-24967">
                Как это проявляется в жизни
              </h4>
              <p className="whitespace-pre-line" data-id="element-24968">{EXTENDED_SUN.dailyLife}</p>

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-24969">
                В отношениях
              </h4>
              <p className="whitespace-pre-line" data-id="element-24970">
                {EXTENDED_SUN.inRelationships}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 my-12 not-prose" data-id="element-24971">
                <div className="bg-emerald-50 border border-emerald-100 rounded-3xl p-8" data-id="element-24972">
                  <h5 className="font-bold text-emerald-800 mb-6 flex items-center gap-3 text-xl" data-id="element-24973">
                    <ShieldCheck className="w-6 h-6" data-id="element-24974" /> Ваши суперсилы
                  </h5>
                  <ul className="space-y-3" data-id="element-24975">
                    {PLANET_INTERPRETATIONS.sun[4].strengths.map((s, i) => <li key={i} className="flex items-start gap-3 text-emerald-700 text-lg" data-id="element-24976">
                        <span className="text-emerald-400 mt-1.5 text-xl" data-id="element-24977">
                          •
                        </span>{' '}
                        {s}
                      </li>)}
                    <li className="flex items-start gap-3 text-emerald-700 text-lg" data-id="element-24978">
                      <span className="text-emerald-400 mt-1.5 text-xl" data-id="element-24979">•</span>{' '}
                      Врожденное благородство
                    </li>
                    <li className="flex items-start gap-3 text-emerald-700 text-lg" data-id="element-24980">
                      <span className="text-emerald-400 mt-1.5 text-xl" data-id="element-24981">•</span>{' '}
                      Умение вдохновлять других
                    </li>
                  </ul>
                </div>
                <div className="bg-rose-50 border border-rose-100 rounded-3xl p-8" data-id="element-24982">
                  <h5 className="font-bold text-rose-800 mb-6 flex items-center gap-3 text-xl" data-id="element-24983">
                    <Zap className="w-6 h-6" data-id="element-24984" /> Теневая сторона
                  </h5>
                  <ul className="space-y-3" data-id="element-24985">
                    {PLANET_INTERPRETATIONS.sun[4].challenges.map((c, i) => <li key={i} className="flex items-start gap-3 text-rose-700 text-lg" data-id="element-24986">
                        <span className="text-rose-400 mt-1.5 text-xl" data-id="element-24987">•</span>{' '}
                        {c}
                      </li>)}
                    <li className="flex items-start gap-3 text-rose-700 text-lg" data-id="element-24988">
                      <span className="text-rose-400 mt-1.5 text-xl" data-id="element-24989">•</span>{' '}
                      Уязвимость к лести
                    </li>
                    <li className="flex items-start gap-3 text-rose-700 text-lg" data-id="element-24990">
                      <span className="text-rose-400 mt-1.5 text-xl" data-id="element-24991">•</span>{' '}
                      Страх показаться слабым
                    </li>
                  </ul>
                </div>
              </div>

              <AdviceBox text="Учитесь отделять свою истинную ценность от аплодисментов окружающих. Ваша задача — светить не ради того, чтобы вам хлопали, а потому что такова ваша природа. Позволяйте себе иногда быть уязвимым — это сделает вас только сильнее в глазах истинно близких людей." data-id="element-24992" />
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-24993" />

          {/* SECTION 04: MOON */}
          <AnimatedSection id="section-04" className="scroll-mt-24" data-id="element-24994">
            <SectionHeader num="04" title="Луна — Внутренний мир" icon={Moon} data-id="element-24995" />

            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-24996">
              <div className="flex items-center gap-5 mb-8 p-6 bg-indigo-50 rounded-2xl border border-indigo-100" data-id="element-24997">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl text-indigo-500 shadow-sm" data-id="element-24998">
                  ♏
                </div>
                <div data-id="element-24999">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-25000">
                    Луна в Скорпионе
                  </h3>
                  <p className="text-indigo-600 font-medium text-lg m-0 mt-1" data-id="element-25001">
                    Падение Луны • Водная стихия
                  </p>
                </div>
              </div>

              <p className="lead text-base sm:text-xl" data-id="element-25002">
                {PLANET_INTERPRETATIONS.moon[7].text}
              </p>

              <h4 className="text-lg sm:text-xl font-bold mt-8 mb-3" data-id="element-25003">
                Эмоциональные паттерны
              </h4>
              <p className="whitespace-pre-line" data-id="element-25004">
                {EXTENDED_MOON.emotionalPatterns}
              </p>

              <PullQuote text="Ваша интуиция граничит с ясновидением. Доверяйте своему первому внутреннему импульсу относительно людей — он редко ошибается." data-id="element-25005" />

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25006">
                Отношения с матерью и детство
              </h4>
              <p className="whitespace-pre-line" data-id="element-25007">
                {EXTENDED_MOON.motherRelation}
              </p>

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25008">
                Скрытые потребности
              </h4>
              <p className="whitespace-pre-line" data-id="element-25009">{EXTENDED_MOON.hiddenNeeds}</p>

              <AdviceBox text="Ваш главный урок — научиться отпускать. Отпускать обиды, контроль, изжившие себя отношения. Не копите эмоциональный яд внутри себя. Найдите экологичные способы сброса напряжения: интенсивный спорт, психотерапия, глубокие исследования или эзотерические практики." data-id="element-25010" />
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25011" />

          {/* SECTION 05: ASCENDANT */}
          <AnimatedSection id="section-05" className="scroll-mt-24" data-id="element-25012">
            <SectionHeader num="05" title="Асцендент — Социальная маска" icon={CircleDot} data-id="element-25013" />

            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25014">
              <div className="flex items-center gap-5 mb-8 p-6 bg-purple-50 rounded-2xl border border-purple-100" data-id="element-25015">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl text-purple-500 shadow-sm" data-id="element-25016">
                  ♐
                </div>
                <div data-id="element-25017">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-25018">
                    Асцендент в Стрельце
                  </h3>
                  <p className="text-purple-600 font-medium text-lg m-0 mt-1" data-id="element-25019">
                    Управитель: Юпитер • Огненная стихия
                  </p>
                </div>
              </div>

              <p className="lead text-base sm:text-xl" data-id="element-25020">
                {ASCENDANT_INTERPRETATIONS[8].text}
              </p>

              <p data-id="element-25021">
                Асцендент — это фильтр, через который вы смотрите на мир, и
                витрина, которую вы показываете окружающим при первом
                знакомстве. Со Стрельцом на Асценденте вы входите в любое
                помещение с энергией оптимизма, расширения и дружелюбия. Люди
                сразу считывают вас как человека открытого, честного и,
                возможно, немного дерзкого.
              </p>

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25022">
                Первое впечатление и внешность
              </h4>
              <p data-id="element-25023">
                Вы часто кажетесь выше своего реального роста благодаря прямой
                осанке и уверенной походке. В ваших движениях есть свобода и
                размах. Вы склонны к активной жестикуляции, а ваша улыбка обычно
                широкая и заразительная. В одежде вы, скорее всего,
                предпочитаете стиль casual, спортивный шик или элементы этники —
                главное, чтобы одежда не сковывала движений и отражала вашу
                любовь к свободе.
              </p>

              <p data-id="element-25024">
                Интересный контраст вашей карты: ваш Асцендент в Стрельце
                обещает людям легкого, веселого и философски настроенного парня.
                Но когда они узнают вас ближе, они сталкиваются с царственным
                эго Солнца во Льве и невероятно глубокой, сложной и
                подозрительной Луной в Скорпионе. Этот диссонанс может удивлять
                ваших партнеров: вы кажетесь гораздо более простым и открытым,
                чем являетесь на самом деле.
              </p>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25025" />

          {/* SECTION 06: MERCURY */}
          <AnimatedSection id="section-06" className="scroll-mt-24" data-id="element-25026">
            <SectionHeader num="06" title="Меркурий — Мышление и коммуникация" icon={Star} data-id="element-25027" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25028">
              <div className="flex items-center gap-4 mb-8" data-id="element-25029">
                <span className="text-4xl" data-id="element-25030">♌</span>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-25031">
                  Меркурий во Льве
                </h3>
              </div>
              <p className="lead text-base sm:text-xl" data-id="element-25032">
                {PLANET_INTERPRETATIONS.mercury[4].text}
              </p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25033">
                Стиль обучения
              </h4>
              <p className="whitespace-pre-line" data-id="element-25034">
                {EXTENDED_MERCURY.learningStyle}
              </p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25035">
                Стиль общения
              </h4>
              <p className="whitespace-pre-line" data-id="element-25036">
                {EXTENDED_MERCURY.communicationStyle}
              </p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25037">
                Принятие решений
              </h4>
              <p className="whitespace-pre-line" data-id="element-25038">
                {EXTENDED_MERCURY.decisionMaking}
              </p>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25039" />

          {/* SECTION 07: VENUS */}
          <AnimatedSection id="section-07" className="scroll-mt-24" data-id="element-25040">
            <SectionHeader num="07" title="Венера — Любовь и ценности" icon={Heart} data-id="element-25041" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25042">
              <div className="flex items-center gap-4 mb-8" data-id="element-25043">
                <span className="text-4xl" data-id="element-25044">♋</span>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-25045">
                  Венера в Раке
                </h3>
              </div>
              <p className="lead text-base sm:text-xl" data-id="element-25046">
                {PLANET_INTERPRETATIONS.venus[3].text}
              </p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25047">
                Язык любви
              </h4>
              <p className="whitespace-pre-line" data-id="element-25048">
                {EXTENDED_VENUS.loveLanguage}
              </p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25049">
                Эстетика и вкус
              </h4>
              <p className="whitespace-pre-line" data-id="element-25050">{EXTENDED_VENUS.aesthetics}</p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25051">
                Финансовые привычки
              </h4>
              <p className="whitespace-pre-line" data-id="element-25052">
                {EXTENDED_VENUS.financialHabits}
              </p>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25053" />

          {/* SECTION 08: MARS */}
          <AnimatedSection id="section-08" className="scroll-mt-24" data-id="element-25054">
            <SectionHeader num="08" title="Марс — Энергия и действие" icon={Zap} data-id="element-25055" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25056">
              <div className="flex items-center gap-4 mb-8" data-id="element-25057">
                <span className="text-4xl" data-id="element-25058">♊</span>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-25059">
                  Марс в Близнецах
                </h3>
              </div>
              <p className="lead text-base sm:text-xl" data-id="element-25060">
                {PLANET_INTERPRETATIONS.mars[2].text}
              </p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25061">
                Стиль конфликта
              </h4>
              <p className="whitespace-pre-line" data-id="element-25062">
                {EXTENDED_MARS.conflictStyle}
              </p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25063">
                Амбиции и работа
              </h4>
              <p className="whitespace-pre-line" data-id="element-25064">
                {EXTENDED_MARS.ambitionStyle}
              </p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25065">
                Физическая энергия
              </h4>
              <p className="whitespace-pre-line" data-id="element-25066">
                {EXTENDED_MARS.physicalEnergy}
              </p>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25067" />

          {/* SECTION 09: JUPITER */}
          <AnimatedSection id="section-09" className="scroll-mt-24" data-id="element-25068">
            <SectionHeader num="09" title="Юпитер — Расширение и удача" icon={ArrowUpRight} data-id="element-25069" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25070">
              <div className="flex items-center gap-4 mb-8" data-id="element-25071">
                <span className="text-4xl" data-id="element-25072">♍</span>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-25073">
                  Юпитер в Деве
                </h3>
              </div>
              <p className="whitespace-pre-line" data-id="element-25074">{EXTENDED_JUPITER}</p>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25075" />

          {/* SECTION 10: SATURN */}
          <AnimatedSection id="section-10" className="scroll-mt-24" data-id="element-25076">
            <SectionHeader num="10" title="Сатурн — Дисциплина и уроки" icon={ShieldCheck} data-id="element-25077" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25078">
              <div className="flex items-center gap-4 mb-8" data-id="element-25079">
                <span className="text-4xl" data-id="element-25080">♒</span>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-25081">
                  Сатурн в Водолее
                </h3>
              </div>
              <p className="whitespace-pre-line" data-id="element-25082">{EXTENDED_SATURN}</p>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25083" />

          {/* SECTION 11: OUTER PLANETS */}
          <AnimatedSection id="section-11" className="scroll-mt-24" data-id="element-25084">
            <SectionHeader num="11" title="Высшие планеты" icon={Globe} data-id="element-25085" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25086">
              <p className="lead text-base sm:text-xl mb-10" data-id="element-25087">
                Высшие планеты двигаются медленно и определяют глобальные тренды
                целых поколений. В вашей личной карте они показывают, как вы
                взаимодействуете с этими глобальными энергиями.
              </p>

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3 flex items-center gap-3" data-id="element-25088">
                <span className="text-3xl" data-id="element-25089">♑</span> Уран в Козероге
              </h4>
              <p className="whitespace-pre-line" data-id="element-25090">
                {OUTER_PLANETS_EXTENDED.uranus}
              </p>

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3 flex items-center gap-3" data-id="element-25091">
                <span className="text-3xl" data-id="element-25092">♑</span> Нептун в Козероге
              </h4>
              <p className="whitespace-pre-line" data-id="element-25093">
                {OUTER_PLANETS_EXTENDED.neptune}
              </p>

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3 flex items-center gap-3" data-id="element-25094">
                <span className="text-3xl" data-id="element-25095">♏</span> Плутон в Скорпионе
              </h4>
              <p className="whitespace-pre-line" data-id="element-25096">
                {OUTER_PLANETS_EXTENDED.pluto}
              </p>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25097" />

          {/* SECTION 12: HOUSES */}
          <AnimatedSection id="section-12" className="scroll-mt-24" data-id="element-25098">
            <SectionHeader num="12" title="Планеты в домах" icon={Compass} data-id="element-25099" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-10" data-id="element-25100">
              <p className="text-base sm:text-xl" data-id="element-25101">
                Дома показывают, в каких сферах жизни будут разворачиваться
                основные события и где проявят себя энергии планет. С
                Асцендентом в Стрельце ваша сетка домов выглядит следующим
                образом:
              </p>
            </div>

            <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-25102">
              {HOUSES_DETAILED.map(house => <motion.div key={house.num} variants={staggerItem} data-id="element-25103">
                  <Card padding="lg" className={`h-full bg-white hover:shadow-lg transition-shadow ${house.planets.length > 0 ? 'border-indigo-200 shadow-md ring-1 ring-indigo-50' : 'border-slate-200 shadow-sm'}`} data-id="element-25104">
                    <div className="flex justify-between items-start mb-4" data-id="element-25105">
                      <div className="flex items-center gap-4" data-id="element-25106">
                        <div className="text-4xl font-black text-slate-200" data-id="element-25107">
                          {house.num}
                        </div>
                        <div data-id="element-25108">
                          <div className="font-bold text-slate-900 text-lg" data-id="element-25109">
                            {house.theme}
                          </div>
                          <div className="text-sm text-slate-500" data-id="element-25110">
                            Знак: {house.sign}
                          </div>
                        </div>
                      </div>
                    </div>

                    {house.planets.length > 0 && <div className="mb-4" data-id="element-25111">
                        <Badge variant="pro" className="bg-indigo-100 text-indigo-700" data-id="element-25112">
                          Планеты: {house.planets.join(', ')}
                        </Badge>
                      </div>}

                    <p className="text-slate-700 leading-relaxed text-base m-0" data-id="element-25113">
                      {house.text}
                    </p>
                  </Card>
                </motion.div>)}
            </motion.div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25114" />

          {/* SECTION 13: ASPECTS */}
          <AnimatedSection id="section-13" className="scroll-mt-24" data-id="element-25115">
            <SectionHeader num="13" title="Аспекты между планетами" icon={Activity} data-id="element-25116" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-10" data-id="element-25117">
              <p className="text-base sm:text-xl" data-id="element-25118">
                Аспекты — это диалоги между планетами. Гармоничные аспекты
                работают как врожденные таланты, а напряженные — как точки роста
                и источники энергии.
              </p>
            </div>

            {/* 6. ASPECT GRID VISUALIZATION */}
            <div className="mb-12 overflow-x-auto pb-4" data-id="element-25119">
              <div className="min-w-[600px] bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-25120">
                <h4 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-25121">
                  <Activity className="w-5 h-5 text-indigo-500" data-id="element-25122" /> Матрица
                  ключевых аспектов
                </h4>
                <div className="grid grid-cols-8 gap-1" data-id="element-25123">
                  {/* Header Row */}
                  <div className="h-10" data-id="element-25124"></div>
                  {['☉', '☽', '☿', '♀', '♂', '♃', '♄'].map((sym, i) => <div key={i} className="h-10 flex items-center justify-center font-bold text-slate-400 text-xl" data-id="element-25125">
                      {sym}
                    </div>)}

                  {/* Rows */}
                  {[{
                  sym: '☉',
                  aspects: [null, null, '☌', null, null, null, null]
                }, {
                  sym: '☽',
                  aspects: [null, null, null, '△', null, null, null]
                }, {
                  sym: '☿',
                  aspects: ['☌', null, null, null, null, null, null]
                }, {
                  sym: '♀',
                  aspects: [null, '△', null, null, null, '⚹', null]
                }, {
                  sym: '♂',
                  aspects: [null, null, null, null, null, '□', '□']
                }, {
                  sym: '♃',
                  aspects: [null, null, null, '⚹', '□', null, null]
                }, {
                  sym: '♄',
                  aspects: [null, null, null, null, '□', null, null]
                }].map((row, i) => <Fragment key={i} data-id="element-25126">
                      <div className="h-10 flex items-center justify-center font-bold text-slate-400 text-xl" data-id="element-25127">
                        {row.sym}
                      </div>
                      {row.aspects.map((aspect, j) => {
                    // Only show lower triangle to avoid duplicates
                    if (j > i) return <div key={j} className="h-10 bg-slate-50/50 rounded-md" data-id="element-25128"></div>;
                    if (j === i) return <div key={j} className="h-10 bg-slate-100 rounded-md" data-id="element-25129"></div>;
                    let bgColor = 'bg-slate-50';
                    let textColor = 'text-slate-300';
                    if (aspect === '☌') {
                      bgColor = 'bg-indigo-100';
                      textColor = 'text-indigo-700';
                    }
                    if (aspect === '△' || aspect === '⚹') {
                      bgColor = 'bg-emerald-100';
                      textColor = 'text-emerald-700';
                    }
                    if (aspect === '□') {
                      bgColor = 'bg-rose-100';
                      textColor = 'text-rose-700';
                    }
                    return <div key={j} className={`h-10 flex items-center justify-center rounded-md font-bold text-lg ${bgColor} ${textColor} transition-transform hover:scale-110 cursor-pointer`} title={`${row.sym} ${aspect} ${['☉', '☽', '☿', '♀', '♂', '♃', '♄'][j]}`} data-id="element-25130">
                            {aspect || '·'}
                          </div>;
                  })}
                    </Fragment>)}
                </div>
                <div className="flex gap-4 mt-6 text-xs text-slate-500 justify-center" data-id="element-25131">
                  <span className="flex items-center gap-1" data-id="element-25132">
                    <span className="w-3 h-3 rounded-full bg-indigo-100 inline-block" data-id="element-25133"></span>{' '}
                    ☌ Соединение
                  </span>
                  <span className="flex items-center gap-1" data-id="element-25134">
                    <span className="w-3 h-3 rounded-full bg-emerald-100 inline-block" data-id="element-25135"></span>{' '}
                    △/⚹ Гармоничные
                  </span>
                  <span className="flex items-center gap-1" data-id="element-25136">
                    <span className="w-3 h-3 rounded-full bg-rose-100 inline-block" data-id="element-25137"></span>{' '}
                    □ Напряженные
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-6" data-id="element-25138">
              {ASPECTS_DETAILED.map((aspect, idx) => <Card key={idx} padding="lg" className={`bg-white border-l-4 hover:shadow-md transition-shadow ${aspect.nature === 'harmonious' ? 'border-l-emerald-500' : aspect.nature === 'tense' ? 'border-l-rose-500' : aspect.nature === 'intense' ? 'border-l-purple-500' : 'border-l-indigo-500'}`} data-id="element-25139">
                  <div className="flex items-center gap-4 mb-4" data-id="element-25140">
                    <Badge variant={aspect.nature === 'harmonious' ? 'success' : aspect.nature === 'tense' ? 'warning' : 'pro'} data-id="element-25141">
                      {aspect.type === 'conjunction' ? 'Соединение' : aspect.type === 'trine' ? 'Трин' : aspect.type === 'sextile' ? 'Секстиль' : 'Квадрат'}
                    </Badge>
                    <h4 className="font-bold text-xl text-slate-900 m-0" data-id="element-25142">
                      {aspect.title}
                    </h4>
                  </div>
                  <p className="text-slate-700 leading-relaxed text-lg m-0" data-id="element-25143">
                    {aspect.text}
                  </p>
                </Card>)}
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25144" />

          {/* SECTION 14: CAREER */}
          <AnimatedSection id="section-14" className="scroll-mt-24" data-id="element-25145">
            <SectionHeader num="14" title="Карьера и финансы" icon={Briefcase} data-id="element-25146" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25147">
              <div className="flex items-center gap-5 mb-8 p-6 bg-slate-50 rounded-2xl border border-slate-200" data-id="element-25148">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl text-slate-500 shadow-sm" data-id="element-25149">
                  <Briefcase className="w-8 h-8" data-id="element-25150" />
                </div>
                <div data-id="element-25151">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-25152">
                    Профессиональный путь
                  </h3>
                  <p className="text-slate-600 font-medium text-lg m-0 mt-1" data-id="element-25153">
                    Юпитер в 10 доме • Уран во 2 доме
                  </p>
                </div>
              </div>

              <p className="lead text-base sm:text-xl" data-id="element-25154">
                Ваш карьерный профиль формируется взаимодействием 10-го дома
                (Дева, Юпитер), 2-го дома финансов (Козерог) и вашего яркого
                Солнца во Льве.
              </p>

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25155">
                Профессиональное призвание
              </h4>
              <p data-id="element-25156">
                С Юпитером в 10-м доме в Деве ваш социальный успех приходит
                через экспертность, аналитику, систематизацию и служение. Вы не
                из тех, кто достигает вершин только за счет харизмы (хотя Солнце
                во Льве дает вам ее в избытке). Ваш авторитет строится на
                высочайшем профессионализме и внимании к деталям.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 my-12 not-prose" data-id="element-25157">
                <div className="bg-indigo-50 border border-indigo-100 rounded-3xl p-8" data-id="element-25158">
                  <h5 className="font-bold text-indigo-900 mb-6 flex items-center gap-3 text-xl" data-id="element-25159">
                    <Target className="w-6 h-6" data-id="element-25160" /> Идеальные сферы
                  </h5>
                  <ul className="space-y-3" data-id="element-25161">
                    <li className="flex items-start gap-3 text-indigo-800 text-lg" data-id="element-25162">
                      <span className="text-indigo-400 mt-1.5 text-xl" data-id="element-25163">•</span>{' '}
                      IT-архитектура и аналитика данных
                    </li>
                    <li className="flex items-start gap-3 text-indigo-800 text-lg" data-id="element-25164">
                      <span className="text-indigo-400 mt-1.5 text-xl" data-id="element-25165">•</span>{' '}
                      Медицина и здравоохранение
                    </li>
                    <li className="flex items-start gap-3 text-indigo-800 text-lg" data-id="element-25166">
                      <span className="text-indigo-400 mt-1.5 text-xl" data-id="element-25167">•</span>{' '}
                      Юриспруденция и консалтинг
                    </li>
                    <li className="flex items-start gap-3 text-indigo-800 text-lg" data-id="element-25168">
                      <span className="text-indigo-400 mt-1.5 text-xl" data-id="element-25169">•</span>{' '}
                      Преподавание и наставничество
                    </li>
                    <li className="flex items-start gap-3 text-indigo-800 text-lg" data-id="element-25170">
                      <span className="text-indigo-400 mt-1.5 text-xl" data-id="element-25171">•</span>{' '}
                      Управление сложными системами
                    </li>
                  </ul>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-3xl p-8" data-id="element-25172">
                  <h5 className="font-bold text-emerald-900 mb-6 flex items-center gap-3 text-xl" data-id="element-25173">
                    <TrendingUp className="w-6 h-6" data-id="element-25174" /> Финансовый профиль
                  </h5>
                  <ul className="space-y-3" data-id="element-25175">
                    <li className="flex items-start gap-3 text-emerald-800 text-lg" data-id="element-25176">
                      <span className="text-emerald-400 mt-1.5 text-xl" data-id="element-25177">•</span>{' '}
                      Постепенный, стабильный рост
                    </li>
                    <li className="flex items-start gap-3 text-emerald-800 text-lg" data-id="element-25178">
                      <span className="text-emerald-400 mt-1.5 text-xl" data-id="element-25179">•</span>{' '}
                      Склонность к консервативным инвестициям
                    </li>
                    <li className="flex items-start gap-3 text-emerald-800 text-lg" data-id="element-25180">
                      <span className="text-emerald-400 mt-1.5 text-xl" data-id="element-25181">•</span>{' '}
                      Нестандартные источники дохода (Уран)
                    </li>
                    <li className="flex items-start gap-3 text-emerald-800 text-lg" data-id="element-25182">
                      <span className="text-emerald-400 mt-1.5 text-xl" data-id="element-25183">•</span>{' '}
                      Заработок через технологии или эзотерику
                    </li>
                  </ul>
                </div>
              </div>

              <p data-id="element-25184">
                Второй дом в Козероге (управляется Сатурном) говорит о том, что
                богатство не свалится на вас с неба. Ваш финансовый рост будет
                постепенным, стабильным и заработанным упорным трудом. Вы
                склонны к консервативным инвестициям и не любите неоправданный
                риск с деньгами. Уран и Нептун во 2-м доме могут давать
                нестандартные источники дохода (например, через технологии,
                интернет или эзотерику).
              </p>
              <AdviceBox text="Ваш ключ к успеху — соединить львиную яркость и амбиции с девьей скрупулезностью. Не бойтесь брать на себя лидерство, но всегда подкрепляйте его реальными знаниями и безупречно выполненной работой." data-id="element-25185" />
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25186" />

          {/* SECTION 15: LOVE */}
          <AnimatedSection id="section-15" className="scroll-mt-24" data-id="element-25187">
            <SectionHeader num="15" title="Любовь и отношения" icon={Heart} data-id="element-25188" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25189">
              <div className="flex items-center gap-5 mb-8 p-6 bg-rose-50 rounded-2xl border border-rose-100" data-id="element-25190">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl text-rose-500 shadow-sm" data-id="element-25191">
                  <Heart className="w-8 h-8 fill-rose-500" data-id="element-25192" />
                </div>
                <div data-id="element-25193">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 m-0" data-id="element-25194">
                    Сфера отношений
                  </h3>
                  <p className="text-rose-600 font-medium text-lg m-0 mt-1" data-id="element-25195">
                    7 дом в Близнецах • Венера в Раке
                  </p>
                </div>
              </div>

              <p className="lead text-base sm:text-xl" data-id="element-25196">
                Ваша сфера отношений полна интересных противоречий. С одной
                стороны, 7-й дом партнерства в Близнецах требует легкости,
                интеллектуального общения и свободы. С другой — Венера в Раке и
                Луна в Скорпионе жаждут глубочайшей эмоциональной привязанности,
                слияния и безопасности.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 my-12 not-prose" data-id="element-25197">
                <div className="bg-pink-50 border border-pink-100 rounded-3xl p-8" data-id="element-25198">
                  <h5 className="font-bold text-pink-900 mb-6 flex items-center gap-3 text-xl" data-id="element-25199">
                    <Star className="w-6 h-6" data-id="element-25200" /> Идеальный партнёр
                  </h5>
                  <ul className="space-y-3" data-id="element-25201">
                    <li className="flex items-start gap-3 text-pink-800 text-lg" data-id="element-25202">
                      <span className="text-pink-400 mt-1.5 text-xl" data-id="element-25203">•</span>{' '}
                      Умный, эрудированный собеседник
                    </li>
                    <li className="flex items-start gap-3 text-pink-800 text-lg" data-id="element-25204">
                      <span className="text-pink-400 mt-1.5 text-xl" data-id="element-25205">•</span>{' '}
                      Обладает хорошим чувством юмора
                    </li>
                    <li className="flex items-start gap-3 text-pink-800 text-lg" data-id="element-25206">
                      <span className="text-pink-400 mt-1.5 text-xl" data-id="element-25207">•</span>{' '}
                      Способен на глубокую эмпатию
                    </li>
                    <li className="flex items-start gap-3 text-pink-800 text-lg" data-id="element-25208">
                      <span className="text-pink-400 mt-1.5 text-xl" data-id="element-25209">•</span>{' '}
                      Ценит верность и домашний уют
                    </li>
                  </ul>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-3xl p-8" data-id="element-25210">
                  <h5 className="font-bold text-amber-900 mb-6 flex items-center gap-3 text-xl" data-id="element-25211">
                    <AlertTriangle className="w-6 h-6" data-id="element-25212" /> Возможные сложности
                  </h5>
                  <ul className="space-y-3" data-id="element-25213">
                    <li className="flex items-start gap-3 text-amber-800 text-lg" data-id="element-25214">
                      <span className="text-amber-400 mt-1.5 text-xl" data-id="element-25215">•</span>{' '}
                      Борьба независимости и привязанности
                    </li>
                    <li className="flex items-start gap-3 text-amber-800 text-lg" data-id="element-25216">
                      <span className="text-amber-400 mt-1.5 text-xl" data-id="element-25217">•</span>{' '}
                      Сильный собственнический инстинкт
                    </li>
                    <li className="flex items-start gap-3 text-amber-800 text-lg" data-id="element-25218">
                      <span className="text-amber-400 mt-1.5 text-xl" data-id="element-25219">•</span>{' '}
                      Страх потери и предательства
                    </li>
                    <li className="flex items-start gap-3 text-amber-800 text-lg" data-id="element-25220">
                      <span className="text-amber-400 mt-1.5 text-xl" data-id="element-25221">•</span>{' '}
                      Склонность к контролю партнера
                    </li>
                  </ul>
                </div>
              </div>

              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25222">
                Динамика отношений
              </h4>
              <p data-id="element-25223">
                Вам нужен человек, который сможет удовлетворить обе эти
                потребности. Внешне и в общении он должен быть легким, умным,
                эрудированным, с хорошим чувством юмора (Близнецы). С ним должно
                быть интересно разговаривать часами. Но за закрытыми дверями
                этот человек должен быть способен на глубокую эмпатию, верность
                и создание уютного, безопасного пространства (Рак/Скорпион).
              </p>
              <p data-id="element-25224">
                Вы можете привлекать партнеров своей легкостью, а затем "душить"
                их контролем или обидами, когда отношения становятся близкими.
              </p>
              <AdviceBox text="Ищите партнера-друга, с которым можно открыто обсуждать любые, даже самые темные эмоции. Ваша задача в отношениях — научиться доверять и не пытаться контролировать партнера из страха предательства." data-id="element-25225" />
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25226" />

          {/* SECTION 16: HEALTH */}
          <AnimatedSection id="section-16" className="scroll-mt-24" data-id="element-25227">
            <SectionHeader num="16" title="Здоровье и энергетика" icon={Activity} data-id="element-25228" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25229">
              <p className="lead text-base sm:text-xl" data-id="element-25230">
                {HEALTH_SECTION.overview}
              </p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25231">
                Солнце и витальность
              </h4>
              <p data-id="element-25232">{HEALTH_SECTION.sunHealth}</p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25233">
                Луна и психосоматика
              </h4>
              <p data-id="element-25234">{HEALTH_SECTION.moonHealth}</p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25235">
                Шестой дом и привычки
              </h4>
              <p data-id="element-25236">{HEALTH_SECTION.sixthHouse}</p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25237">
                Марс и нервная система
              </h4>
              <p data-id="element-25238">{HEALTH_SECTION.marsHealth}</p>

              <div className="bg-emerald-50 border border-emerald-100 rounded-3xl p-8 mt-10 not-prose" data-id="element-25239">
                <h5 className="font-bold text-emerald-800 mb-6 flex items-center gap-3 text-xl" data-id="element-25240">
                  <ShieldCheck className="w-6 h-6" data-id="element-25241" /> Рекомендации для здоровья
                </h5>
                <p className="whitespace-pre-line text-emerald-800 text-lg leading-relaxed" data-id="element-25242">
                  {HEALTH_SECTION.recommendations}
                </p>
              </div>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25243" />

          {/* SECTION 17: KARMIC */}
          <AnimatedSection id="section-17" className="scroll-mt-24" data-id="element-25244">
            <SectionHeader num="17" title="Кармические задачи" icon={Target} data-id="element-25245" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25246">
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25247">
                Северный узел (Куда идти)
              </h4>
              <p className="whitespace-pre-line" data-id="element-25248">{KARMIC_SECTION.northNode}</p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25249">
                Южный узел (Что оставить)
              </h4>
              <p className="whitespace-pre-line" data-id="element-25250">{KARMIC_SECTION.southNode}</p>
              <h4 className="text-lg sm:text-xl font-bold mt-10 mb-3" data-id="element-25251">
                Уроки Сатурна
              </h4>
              <p className="whitespace-pre-line" data-id="element-25252">
                {KARMIC_SECTION.saturnLesson}
              </p>
              <PullQuote text={KARMIC_SECTION.lifeMission} data-id="element-25253" />
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25254" />

          {/* SECTION 18: TALENTS */}
          <AnimatedSection id="section-18" className="scroll-mt-24" data-id="element-25255">
            <SectionHeader num="18" title="Таланты и предназначение" icon={Award} data-id="element-25256" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25257">
              <p className="whitespace-pre-line" data-id="element-25258">
                {TALENTS_SECTION.naturalAbilities}
              </p>
              <div className="bg-indigo-50 border border-indigo-100 rounded-3xl p-8 mt-10 not-prose" data-id="element-25259">
                <h5 className="font-bold text-indigo-900 mb-6 text-xl" data-id="element-25260">
                  Лучшие сферы реализации
                </h5>
                <p className="whitespace-pre-line text-indigo-800 text-lg leading-relaxed" data-id="element-25261">
                  {TALENTS_SECTION.bestAreas}
                </p>
              </div>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25262" />

          {/* SECTION 19: CHALLENGES */}
          <AnimatedSection id="section-19" className="scroll-mt-24" data-id="element-25263">
            <SectionHeader num="19" title="Жизненные вызовы" icon={AlertTriangle} data-id="element-25264" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25265">
              <p className="whitespace-pre-line" data-id="element-25266">
                {CHALLENGES_SECTION.mainChallenges}
              </p>
              <AdviceBox text={CHALLENGES_SECTION.recommendations} data-id="element-25267" />
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25268" />

          {/* SECTION 20: FORECAST */}
          <AnimatedSection id="section-20" className="scroll-mt-24" data-id="element-25269">
            <SectionHeader num="20" title="Прогноз жизненных периодов" icon={TrendingUp} data-id="element-25270" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-10" data-id="element-25271">
              <p className="text-base sm:text-xl" data-id="element-25272">
                {FORECAST_SECTION.overview}
              </p>
            </div>

            <div className="relative border-l-4 border-indigo-200 ml-6 space-y-10 py-4" data-id="element-25273">
              {FORECAST_SECTION.periods.map((period, idx) => <motion.div key={idx} className="relative pl-8 group" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: idx * 0.1
            }} data-id="element-25274">
                  <div className="absolute -left-[14px] top-1 w-6 h-6 rounded-full bg-indigo-500 border-4 border-white shadow-sm group-hover:scale-125 transition-transform duration-300" data-id="element-25275"></div>
                  <Badge variant="pro" className="mb-2 bg-indigo-100 text-indigo-700" data-id="element-25276">
                    {period.age}
                  </Badge>
                  <h4 className="text-xl font-bold text-slate-900 mb-2" data-id="element-25277">
                    {period.title}
                  </h4>
                  <p className="text-slate-700 leading-relaxed text-lg" data-id="element-25278">
                    {period.text}
                  </p>
                </motion.div>)}
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25279" />

          {/* SECTION 21: FINAL ANALYSIS */}
          <AnimatedSection id="section-21" className="scroll-mt-24" data-id="element-25280">
            <SectionHeader num="21" title="Итоговый анализ" icon={Star} data-id="element-25281" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none" data-id="element-25282">
              <p className="whitespace-pre-line text-base sm:text-xl leading-relaxed" data-id="element-25283">
                {FINAL_ANALYSIS.summary}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-12 not-prose" data-id="element-25284">
                <div className="bg-indigo-50/70 rounded-2xl p-6 md:p-8 border border-indigo-200 border-l-4 border-l-indigo-500" data-id="element-25285">
                  <h4 className="font-bold text-lg md:text-xl mb-5 text-indigo-700" data-id="element-25286">
                    Ключевые качества
                  </h4>
                  <ul className="space-y-3" data-id="element-25287">
                    {FINAL_ANALYSIS.keyQualities.map((q, i) => <li key={i} className="flex gap-3" data-id="element-25288">
                        <Sparkles className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-1" data-id="element-25289" />
                        <span className="text-base text-slate-800" data-id="element-25290">{q}</span>
                      </li>)}
                  </ul>
                </div>
                <div className="bg-amber-50/70 rounded-2xl p-6 md:p-8 border border-amber-200 border-l-4 border-l-amber-500" data-id="element-25291">
                  <h4 className="font-bold text-lg md:text-xl mb-5 text-amber-700" data-id="element-25292">
                    Главные темы жизни
                  </h4>
                  <ul className="space-y-3" data-id="element-25293">
                    {FINAL_ANALYSIS.lifeThemes.map((t, i) => <li key={i} className="flex gap-3" data-id="element-25294">
                        <Compass className="w-4 h-4 text-amber-500 flex-shrink-0 mt-1" data-id="element-25295" />
                        <span className="text-base text-slate-800" data-id="element-25296">{t}</span>
                      </li>)}
                  </ul>
                </div>
              </div>

              <div className="my-10 p-6 md:p-8 bg-gradient-to-br from-amber-100 via-yellow-50 to-amber-100 rounded-2xl shadow-sm border border-amber-200/60 text-center relative overflow-hidden not-prose" data-id="element-25297">
                <Sparkles className="w-8 h-8 text-amber-500 mx-auto mb-4" data-id="element-25298" />
                <p className="text-base md:text-lg font-semibold leading-relaxed text-slate-800 italic max-w-2xl mx-auto relative z-10" data-id="element-25299">
                  {FINAL_ANALYSIS.finalAdvice}
                </p>
              </div>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25300" />

          {/* SECTION 22: ENERGY RADAR */}
          <AnimatedSection id="section-22" className="scroll-mt-24" data-id="element-25301">
            <SectionHeader num="22" title="Шкала энергий" icon={Activity} data-id="element-25302" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-10" data-id="element-25303">
              <p className="text-base sm:text-xl" data-id="element-25304">
                Ваш энергетический профиль показывает баланс ключевых качеств,
                заложенных в натальной карте. Это визуальное представление ваших
                сильных сторон.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-4 sm:p-6 md:p-10 mb-12" data-id="element-25305">
              <div className="flex flex-col lg:flex-row items-center gap-6 md:gap-10" data-id="element-25306">
                {/* Radar Chart SVG */}
                <div className="w-full max-w-[280px] sm:max-w-[320px] md:max-w-[360px] lg:max-w-[400px] aspect-square relative flex-shrink-0 mx-auto lg:mx-0" data-id="element-25307">
                  <svg viewBox="-50 -30 300 260" className="w-full h-full" data-id="element-25308">
                    {/* Background grid */}
                    {[20, 40, 60, 80, 100].map((radius, i) => <polygon key={i} points={ENERGY_RADAR_DATA.axes.map((_, index) => {
                    const angle = Math.PI * 2 * index / 6 - Math.PI / 2;
                    const x = 100 + radius * Math.cos(angle);
                    const y = 100 + radius * Math.sin(angle);
                    return `${x},${y}`;
                  }).join(' ')} fill="none" stroke="#e2e8f0" strokeWidth="1" data-id="element-25309" />)}

                    {/* Axes lines */}
                    {ENERGY_RADAR_DATA.axes.map((_, index) => {
                    const angle = Math.PI * 2 * index / 6 - Math.PI / 2;
                    const x = 100 + 100 * Math.cos(angle);
                    const y = 100 + 100 * Math.sin(angle);
                    return <line key={index} x1="100" y1="100" x2={x} y2={y} stroke="#cbd5e1" strokeWidth="1" strokeDasharray="4 4" data-id="element-25310" />;
                  })}

                    {/* Data polygon */}
                    <polygon points={ENERGY_RADAR_DATA.axes.map((axis, index) => {
                    const angle = Math.PI * 2 * index / 6 - Math.PI / 2;
                    const radius = axis.value;
                    const x = 100 + radius * Math.cos(angle);
                    const y = 100 + radius * Math.sin(angle);
                    return `${x},${y}`;
                  }).join(' ')} fill="rgba(99, 102, 241, 0.2)" stroke="#6366f1" strokeWidth="2" data-id="element-25311" />

                    {/* Data points */}
                    {ENERGY_RADAR_DATA.axes.map((axis, index) => {
                    const angle = Math.PI * 2 * index / 6 - Math.PI / 2;
                    const radius = axis.value;
                    const x = 100 + radius * Math.cos(angle);
                    const y = 100 + radius * Math.sin(angle);
                    return <circle key={index} cx={x} cy={y} r="4" fill="#4f46e5" className="transition-all duration-300 hover:r-6 cursor-pointer" data-id="element-25312">
                          <title data-id="element-25313">
                            {axis.label}: {axis.value}%
                          </title>
                        </circle>;
                  })}

                    {/* Labels */}
                    {ENERGY_RADAR_DATA.axes.map((axis, index) => {
                    const angle = Math.PI * 2 * index / 6 - Math.PI / 2;
                    const radius = 120;
                    const x = 100 + radius * Math.cos(angle);
                    const y = 100 + radius * Math.sin(angle);
                    let textAnchor = 'middle';
                    if (x < 90) textAnchor = 'end';
                    if (x > 110) textAnchor = 'start';
                    return <text key={index} x={x} y={y} textAnchor={textAnchor} dominantBaseline="middle" className="text-[9px] sm:text-[10px] font-bold fill-slate-700" data-id="element-25314">
                          {axis.label}
                        </text>;
                  })}
                  </svg>
                </div>

                {/* Axis Descriptions */}
                <div className="w-full lg:flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-id="element-25315">
                  {ENERGY_RADAR_DATA.axes.map((axis, i) => <div key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-25316">
                      <div className="flex justify-between items-center mb-2" data-id="element-25317">
                        <span className="font-bold text-slate-900" data-id="element-25318">
                          {axis.label}
                        </span>
                        <span className="text-indigo-600 font-mono text-sm font-bold" data-id="element-25319">
                          {axis.value}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5 mb-3 overflow-hidden" data-id="element-25320">
                        <motion.div className="bg-indigo-500 h-1.5 rounded-full" initial={{
                      width: 0
                    }} whileInView={{
                      width: `${axis.value}%`
                    }} viewport={{
                      once: true
                    }} transition={{
                      duration: 1,
                      delay: i * 0.1,
                      ease: 'easeOut'
                    }} data-id="element-25321"></motion.div>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed m-0" data-id="element-25322">
                        {axis.description}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25323" />

          {/* SECTION 23: COMPATIBILITY */}
          <AnimatedSection id="section-23" className="scroll-mt-24" data-id="element-25324">
            <SectionHeader num="23" title="Совместимость по знакам" icon={Heart} data-id="element-25325" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-10" data-id="element-25326">
              <p className="text-base sm:text-xl" data-id="element-25327">
                Краткий гид по вашей совместимости с представителями разных
                знаков зодиака, основанный на анализе ваших Солнца, Луны и
                Венеры.
              </p>
            </div>

            <motion.div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-25328">
              {ZODIAC_COMPATIBILITY.map((item, i) => {
              const isHigh = item.percent >= 80;
              const isMedium = item.percent >= 65 && item.percent < 80;
              const colorClass = isHigh ? 'text-emerald-600' : isMedium ? 'text-amber-600' : 'text-rose-600';
              const bgClass = isHigh ? 'bg-emerald-500' : isMedium ? 'bg-amber-500' : 'bg-rose-500';
              return <motion.div key={i} variants={staggerItem} className={`bg-white rounded-2xl p-5 border hover:shadow-md transition-shadow ${item.best ? 'border-indigo-300 shadow-md ring-1 ring-indigo-50 relative' : 'border-slate-200 shadow-sm'}`} data-id="element-25329">
                    {item.best && <div className="absolute -top-2 -right-2 bg-indigo-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm" data-id="element-25330">
                        Топ
                      </div>}
                    <div className="flex justify-between items-center mb-3" data-id="element-25331">
                      <div className="flex items-center gap-2" data-id="element-25332">
                        <span className={`text-2xl ${colorClass}`} data-id="element-25333">
                          {item.symbol}
                        </span>
                        <span className="font-bold text-slate-900" data-id="element-25334">
                          {item.sign}
                        </span>
                      </div>
                      <span className={`font-mono font-bold text-sm ${colorClass}`} data-id="element-25335">
                        {item.percent}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3 overflow-hidden" data-id="element-25336">
                      <motion.div className={`${bgClass} h-1.5 rounded-full`} initial={{
                    width: 0
                  }} whileInView={{
                    width: `${item.percent}%`
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: i * 0.05,
                    ease: 'easeOut'
                  }} data-id="element-25337"></motion.div>
                    </div>
                    <p className="text-xs text-slate-500 leading-snug m-0" data-id="element-25338">
                      {item.note}
                    </p>
                  </motion.div>;
            })}
            </motion.div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25339" />

          {/* SECTION 24: AFFIRMATIONS */}
          <AnimatedSection id="section-24" className="scroll-mt-24" data-id="element-25340">
            <SectionHeader num="24" title="Персональные аффирмации" icon={Sparkles} data-id="element-25341" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-10" data-id="element-25342">
              <p className="text-base sm:text-xl" data-id="element-25343">
                Специально подобранные фразы-настройки, которые помогают
                гармонизировать напряжённые аспекты вашей карты и усилить
                таланты.
              </p>
            </div>

            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-25344">
              {PERSONAL_AFFIRMATIONS.map((aff, i) => <motion.div key={i} variants={staggerItem} className={`bg-gradient-to-br ${aff.gradient} rounded-3xl p-8 border border-white shadow-sm relative overflow-hidden group hover:shadow-md transition-all`} data-id="element-25345">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/40 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" data-id="element-25346"></div>
                  <div className="mb-6 text-indigo-300 opacity-50 group-hover:opacity-100 group-hover:scale-110 transition-all" data-id="element-25347">
                    {aff.icon === 'moon' && <Moon className="w-8 h-8" data-id="element-25348" />}
                    {aff.icon === 'sun' && <Sun className="w-8 h-8" data-id="element-25349" />}
                    {aff.icon === 'sparkles' && <Sparkles className="w-8 h-8" data-id="element-25350" />}
                    {aff.icon === 'target' && <Target className="w-8 h-8" data-id="element-25351" />}
                    {aff.icon === 'compass' && <Compass className="w-8 h-8" data-id="element-25352" />}
                    {aff.icon === 'heart' && <Heart className="w-8 h-8" data-id="element-25353" />}
                  </div>
                  <p className="text-lg font-medium text-slate-800 mb-6 leading-relaxed relative z-10" data-id="element-25354">
                    «{aff.text}»
                  </p>
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider relative z-10" data-id="element-25355">
                    {aff.source}
                  </div>
                </motion.div>)}
            </motion.div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25356" />

          {/* SECTION 25: TRANSITS */}
          <AnimatedSection id="section-25" className="scroll-mt-24" data-id="element-25357">
            <SectionHeader num="25" title="Транзиты текущего месяца" icon={Clock} data-id="element-25358" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-10" data-id="element-25359">
              <p className="text-base sm:text-xl" data-id="element-25360">
                Что происходит на небе прямо сейчас и как это влияет лично на
                вас. Краткий прогноз на ближайшие недели.
              </p>
            </div>

            <div className="relative border-l-2 border-slate-200 ml-4 md:ml-8 space-y-8 py-4" data-id="element-25361">
              {CURRENT_TRANSITS.map((transit, i) => {
              const isHarmonious = transit.nature === 'harmonious';
              const isTense = transit.nature === 'tense';
              const colorClass = isHarmonious ? 'bg-emerald-500' : isTense ? 'bg-amber-500' : 'bg-indigo-500';
              const bgClass = isHarmonious ? 'bg-emerald-50 border-emerald-100' : isTense ? 'bg-amber-50 border-amber-100' : 'bg-indigo-50 border-indigo-100';
              const textClass = isHarmonious ? 'text-emerald-800' : isTense ? 'text-amber-800' : 'text-indigo-800';
              return <motion.div key={i} className="relative pl-8 md:pl-12 group" initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5,
                delay: i * 0.1
              }} data-id="element-25362">
                    <div className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full ${colorClass} border-4 border-white shadow-sm group-hover:scale-125 transition-transform duration-300`} data-id="element-25363"></div>

                    <div className={`rounded-2xl p-6 border ${bgClass} shadow-sm hover:shadow-md transition-shadow`} data-id="element-25364">
                      <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-25365">
                        <Badge variant="neutral" className="bg-white text-slate-700 font-bold" data-id="element-25366">
                          {transit.dateRange}
                        </Badge>
                        <span className={`text-sm font-bold uppercase tracking-wider ${textClass}`} data-id="element-25367">
                          {transit.area}
                        </span>
                      </div>
                      <h4 className="text-xl font-bold text-slate-900 mb-3" data-id="element-25368">
                        {transit.transit}
                      </h4>
                      <p className="text-slate-700 leading-relaxed m-0" data-id="element-25369">
                        {transit.recommendation}
                      </p>
                    </div>
                  </motion.div>;
            })}
            </div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25370" />

          {/* SECTION 26: CELEBRITIES */}
          <AnimatedSection id="section-26" className="scroll-mt-24" data-id="element-25371">
            <SectionHeader num="26" title="Знаменитости с похожей картой" icon={Star} data-id="element-25372" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-10" data-id="element-25373">
              <p className="text-base sm:text-xl" data-id="element-25374">
                Известные люди, которые разделяют с вами ключевые
                астрологические показатели. Посмотрите, как они реализовали этот
                потенциал.
              </p>
            </div>

            <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-25375">
              {CELEBRITY_MATCHES.map((celeb, i) => <motion.div key={i} variants={staggerItem} className="flex items-center gap-4 p-4 rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-25376">
                  <div className={`w-12 h-12 rounded-full ${celeb.color} flex items-center justify-center text-base font-bold flex-shrink-0`} data-id="element-25377">
                    {celeb.initials}
                  </div>
                  <div className="min-w-0" data-id="element-25378">
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-25379">
                      {celeb.name}
                    </h4>
                    <div className="text-xs font-medium text-indigo-600 mb-0.5 leading-snug" data-id="element-25380">
                      {celeb.sharedPlacement}
                    </div>
                    <p className="text-[11px] text-slate-500 leading-snug m-0" data-id="element-25381">
                      {celeb.note}
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>
          </AnimatedSection>

          <ChapterDivider data-id="element-25382" />

          {/* SECTION 27: VISUAL CHART */}
          <AnimatedSection id="section-27" className="scroll-mt-24" data-id="element-25383">
            <SectionHeader num="27" title="Визуальная карта натала" icon={Compass} data-id="element-25384" />
            <div className="prose prose-base sm:prose-lg prose-slate max-w-none mb-10" data-id="element-25385">
              <p className="text-base sm:text-xl" data-id="element-25386">
                Классическое круговое отображение вашей натальной карты. Внешний
                круг — знаки зодиака, внутренний — 12 домов, символы — планеты в
                момент вашего рождения.
              </p>
            </div>

            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-4 md:p-10 flex justify-center" data-id="element-25387">
              <div className="w-full max-w-[500px] aspect-square relative" data-id="element-25388">
                <svg viewBox="0 0 400 400" className="w-full h-full drop-shadow-sm" data-id="element-25389">
                  {/* Outer ring (Zodiac signs) */}
                  <circle cx="200" cy="200" r="180" fill="none" stroke="#e2e8f0" strokeWidth="40" data-id="element-25390" />

                  {/* Zodiac segments and symbols */}
                  {NATAL_CHART_VISUAL.signs.map((sign, i) => {
                  // SVG arcs for each sign (30 degrees each)
                  const startAngle = (sign.startDeg - 90) * (Math.PI / 180);
                  const endAngle = (sign.startDeg + 30 - 90) * (Math.PI / 180);
                  const x1 = 200 + 180 * Math.cos(startAngle);
                  const y1 = 200 + 180 * Math.sin(startAngle);
                  const x2 = 200 + 180 * Math.cos(endAngle);
                  const y2 = 200 + 180 * Math.sin(endAngle);
                  // Center point for the symbol
                  const midAngle = (sign.startDeg + 15 - 90) * (Math.PI / 180);
                  const symX = 200 + 180 * Math.cos(midAngle);
                  const symY = 200 + 180 * Math.sin(midAngle);
                  return <g key={`sign-${i}`} data-id="element-25391">
                        {/* Separator lines */}
                        <line x1="200" y1="200" x2={200 + 200 * Math.cos(startAngle)} y2={200 + 200 * Math.sin(startAngle)} stroke="#cbd5e1" strokeWidth="1" data-id="element-25392" />
                        {/* Symbol */}
                        <text x={symX} y={symY} textAnchor="middle" dominantBaseline="middle" fontSize="20" fill={sign.color} fontWeight="bold" data-id="element-25393">
                          {sign.symbol}
                        </text>
                      </g>;
                })}

                  {/* Inner ring (Houses) */}
                  <circle cx="200" cy="200" r="140" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="2" data-id="element-25394" />
                  <circle cx="200" cy="200" r="60" fill="white" stroke="#cbd5e1" strokeWidth="2" data-id="element-25395" />

                  {/* House numbers (simplified equal houses for visual representation) */}
                  {/* Ascendant is at 240 deg (Sagittarius), so House 1 starts there */}
                  {[...Array(12)].map((_, i) => {
                  const houseStartDeg = NATAL_CHART_VISUAL.ascendant.degree + i * 30;
                  const startAngle = (houseStartDeg - 90) * (Math.PI / 180);
                  const midAngle = (houseStartDeg + 15 - 90) * (Math.PI / 180);
                  const numX = 200 + 75 * Math.cos(midAngle);
                  const numY = 200 + 75 * Math.sin(midAngle);
                  return <g key={`house-${i}`} data-id="element-25396">
                        <line x1={200 + 60 * Math.cos(startAngle)} y1={200 + 60 * Math.sin(startAngle)} x2={200 + 140 * Math.cos(startAngle)} y2={200 + 140 * Math.sin(startAngle)} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4" data-id="element-25397" />
                        <text x={numX} y={numY} textAnchor="middle" dominantBaseline="middle" fontSize="12" fill="#94a3b8" fontWeight="bold" data-id="element-25398">
                          {i + 1}
                        </text>
                      </g>;
                })}

                  {/* Ascendant Line (Horizon) */}
                  <line x1={200 + 200 * Math.cos((NATAL_CHART_VISUAL.ascendant.degree - 90) * (Math.PI / 180))} y1={200 + 200 * Math.sin((NATAL_CHART_VISUAL.ascendant.degree - 90) * (Math.PI / 180))} x2={200 + 200 * Math.cos((NATAL_CHART_VISUAL.ascendant.degree + 180 - 90) * (Math.PI / 180))} y2={200 + 200 * Math.sin((NATAL_CHART_VISUAL.ascendant.degree + 180 - 90) * (Math.PI / 180))} stroke="#ef4444" strokeWidth="2" data-id="element-25399" />
                  <text x={200 + 150 * Math.cos((NATAL_CHART_VISUAL.ascendant.degree - 90) * (Math.PI / 180))} y={200 + 150 * Math.sin((NATAL_CHART_VISUAL.ascendant.degree - 90) * (Math.PI / 180)) - 10} textAnchor="middle" fontSize="12" fill="#ef4444" fontWeight="bold" data-id="element-25400">
                    ASC
                  </text>

                  {/* Planets */}
                  {NATAL_CHART_VISUAL.planets.map((planet, i) => {
                  const angle = (planet.degree - 90) * (Math.PI / 180);
                  // Distribute planets radially to avoid overlap (simple approximation)
                  const radius = 100 + i % 3 * 15;
                  const x = 200 + radius * Math.cos(angle);
                  const y = 200 + radius * Math.sin(angle);
                  return <g key={`planet-${i}`} className="cursor-pointer transition-transform hover:scale-125 origin-center" style={{
                    transformOrigin: `${x}px ${y}px`
                  }} data-id="element-25401">
                        <circle cx={x} cy={y} r="12" fill="white" stroke="#6366f1" strokeWidth="1" data-id="element-25402" />
                        <text x={x} y={y} textAnchor="middle" dominantBaseline="central" fontSize="14" fill="#4f46e5" fontWeight="bold" data-id="element-25403">
                          {planet.symbol}
                        </text>
                        <title data-id="element-25404">
                          {planet.name} в {planet.sign}
                        </title>
                      </g>;
                })}

                  {/* Center dot */}
                  <circle cx="200" cy="200" r="4" fill="#64748b" data-id="element-25405" />
                </svg>
              </div>
            </div>
            <p className="text-center text-sm text-slate-500 mt-4" data-id="element-25406">
              Наведите курсор на символы планет для подсказки
            </p>
          </AnimatedSection>

          {/* APPENDIX: PLANET POSITIONS */}
          <AnimatedSection className="mt-32 pt-16 border-t border-slate-200" data-id="element-25407">
            <h3 className="text-2xl font-bold text-slate-900 mb-4 text-center" data-id="element-25408">
              Приложение: Положения планет
            </h3>
            <p className="text-center text-slate-500 mb-8 max-w-2xl mx-auto" data-id="element-25409">
              Эта таблица показывает точные координаты каждой планеты в момент
              вашего рождения. Знак определяет стиль проявления, градус — точное
              положение, дом — сферу жизни, а достоинство — силу планеты в
              данном знаке.
            </p>

            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-4 mb-8" data-id="element-25410">
              <div className="flex items-center gap-2 text-sm" data-id="element-25411">
                <span className="w-3 h-3 rounded-full bg-emerald-500" data-id="element-25412"></span>
                <span className="text-slate-600" data-id="element-25413">
                  <strong data-id="element-25414">Обитель</strong> — планета в своём знаке, максимальная
                  сила
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="element-25415">
                <span className="w-3 h-3 rounded-full bg-amber-500" data-id="element-25416"></span>
                <span className="text-slate-600" data-id="element-25417">
                  <strong data-id="element-25418">Изгнание</strong> — планета в противоположном знаке,
                  требует проработки
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm" data-id="element-25419">
                <span className="w-3 h-3 rounded-full bg-rose-500" data-id="element-25420"></span>
                <span className="text-slate-600" data-id="element-25421">
                  <strong data-id="element-25422">Падение</strong> — ослабленная позиция, скрытый
                  потенциал
                </span>
              </div>
            </div>

            <div className="overflow-x-auto -mx-4 sm:mx-0 bg-white sm:rounded-2xl shadow-sm border border-slate-200" data-id="element-25423">
              <table className="w-full text-left text-sm sm:text-base lg:text-lg" data-id="element-25424">
                <thead className="bg-slate-50 text-slate-600 border-b border-slate-200" data-id="element-25425">
                  <tr data-id="element-25426">
                    <th className="px-3 sm:px-6 py-3 sm:py-4 font-bold sm:rounded-tl-xl" data-id="element-25427">
                      Планета
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 font-bold" data-id="element-25428">
                      Знак
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 font-bold" data-id="element-25429">
                      Градус
                    </th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 font-bold" data-id="element-25430">Дом</th>
                    <th className="px-3 sm:px-6 py-3 sm:py-4 sm:rounded-tr-xl" data-id="element-25431">
                      Достоинство
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100" data-id="element-25432">
                  {PLANET_POSITIONS_TABLE.map((row, i) => <tr key={i} className="hover:bg-slate-50 transition-colors" data-id="element-25433">
                      <td className="px-3 sm:px-6 py-3 sm:py-4 font-medium text-slate-900 flex items-center gap-2 sm:gap-3" data-id="element-25434">
                        <span className="text-lg sm:text-2xl text-indigo-500 w-6 sm:w-8 text-center" data-id="element-25435">
                          {row.symbol}
                        </span>{' '}
                        {row.planet}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-slate-700" data-id="element-25436">
                        {row.sign}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-slate-500 font-mono text-xs sm:text-sm" data-id="element-25437">
                        {row.degree}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 text-slate-700 font-bold" data-id="element-25438">
                        {row.house}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4" data-id="element-25439">
                        {row.dignity === 'Обитель' ? <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium bg-emerald-50 px-3 py-1 rounded-full text-sm" data-id="element-25440">
                            <span className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-25441"></span>
                            {row.dignity}
                          </span> : row.dignity === 'Изгнание' ? <span className="inline-flex items-center gap-1.5 text-amber-700 font-medium bg-amber-50 px-3 py-1 rounded-full text-sm" data-id="element-25442">
                            <span className="w-2 h-2 rounded-full bg-amber-500" data-id="element-25443"></span>
                            {row.dignity}
                          </span> : row.dignity === 'Падение' ? <span className="inline-flex items-center gap-1.5 text-rose-700 font-medium bg-rose-50 px-3 py-1 rounded-full text-sm" data-id="element-25444">
                            <span className="w-2 h-2 rounded-full bg-rose-500" data-id="element-25445"></span>
                            {row.dignity}
                          </span> : <span className="text-slate-400" data-id="element-25446">{row.dignity}</span>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </AnimatedSection>

          {/* GLOSSARY SECTION */}
          <AnimatedSection className="mt-20 pt-16 border-t border-slate-200" data-id="element-25447">
            <h3 className="text-2xl font-bold text-slate-900 mb-4 text-center" data-id="element-25448">
              Словарь терминов
            </h3>
            <p className="text-center text-slate-500 mb-10 max-w-2xl mx-auto" data-id="element-25449">
              Краткое пояснение ключевых астрологических понятий, используемых в
              этом отчёте
            </p>
            <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-25450">
              {[{
              term: 'Натальная карта',
              desc: 'Карта неба на момент рождения. Показывает расположение планет по знакам зодиака и домам.'
            }, {
              term: 'Асцендент (ASC)',
              desc: 'Знак, восходивший на восточном горизонте в момент рождения. Определяет внешнее поведение и первое впечатление.'
            }, {
              term: 'Большая тройка',
              desc: 'Солнце (эго), Луна (эмоции) и Асцендент (маска) — три главных элемента личности.'
            }, {
              term: 'Дома гороскопа',
              desc: '12 секторов карты, каждый отвечает за определённую сферу жизни: от личности (1 дом) до подсознания (12 дом).'
            }, {
              term: 'Аспекты',
              desc: 'Углы между планетами. Трин (△) и секстиль (⚹) — гармоничные, квадрат (□) — напряжённый, соединение (☌) — усиление.'
            }, {
              term: 'Обитель / Изгнание / Падение',
              desc: 'Достоинства планет. В обители планета сильна, в изгнании — ослаблена, в падении — работает через преодоление.'
            }, {
              term: 'Стихии',
              desc: 'Огонь (действие), Земля (практичность), Воздух (общение), Вода (эмоции) — четыре базовых энергии знаков.'
            }, {
              term: 'Кресты (модальности)',
              desc: 'Кардинальный (инициатива), Фиксированный (стабильность), Мутабельный (адаптация) — три режима действия знаков.'
            }, {
              term: 'Лунные узлы',
              desc: 'Северный узел — направление развития, Южный узел — привычные паттерны из прошлого, которые нужно отпустить.'
            }, {
              term: 'Возврат Сатурна',
              desc: 'Происходит ~каждые 29 лет. Ключевой кризис взросления, переоценка жизненных целей и структур.'
            }].map((item, i) => <motion.div key={i} variants={staggerItem} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-sm transition-shadow" data-id="element-25451">
                  <h4 className="font-bold text-slate-900 mb-2 text-base" data-id="element-25452">
                    {item.term}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed m-0" data-id="element-25453">
                    {item.desc}
                  </p>
                </motion.div>)}
            </motion.div>
          </AnimatedSection>
        </div>
      </main>

      {/* CLOSING BLOCK */}
      <div ref={footerRef} className="bg-slate-900 text-white py-16 sm:py-24 relative z-10 overflow-hidden" data-id="element-25454">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-25455">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-full bg-gradient-to-b from-indigo-500/10 to-transparent blur-3xl" data-id="element-25456" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" data-id="element-25457" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" data-id="element-25458" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10" data-id="element-25459">
          <div className="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-indigo-300 text-sm font-medium mb-8" data-id="element-25460">
            <Sparkles className="w-4 h-4" data-id="element-25461" />
            Конец отчёта
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-white" data-id="element-25462">
            Спасибо за внимание к отчёту
          </h2>

          <p className="text-slate-300/80 leading-relaxed text-lg sm:text-xl max-w-2xl mx-auto mb-12 font-medium" data-id="element-25463">
            Используйте этот отчёт как инструмент самопознания. Возвращайтесь к
            нему в разные периоды жизни — вы будете находить новые смыслы и
            подтверждения.
          </p>

          <div className="bg-white/[0.03] backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/10 max-w-2xl mx-auto shadow-2xl relative overflow-hidden group" data-id="element-25464">
            <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-amber-400 to-amber-600" data-id="element-25465" />
            <div className="absolute -right-8 -top-8 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/20 transition-colors duration-500" data-id="element-25466" />
            <p className="text-amber-100/90 italic leading-relaxed text-lg sm:text-xl font-medium relative z-10" data-id="element-25467">
              «{FINAL_ANALYSIS.finalAdvice}»
            </p>
          </div>
        </div>
      </div>

      {/* REPORT FOOTER */}
      <div className="bg-slate-50 border-t border-slate-200 py-4 sm:py-5 relative z-10" data-id="element-25468">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-25469">
          <div className="text-center sm:text-left" data-id="element-25470">
            <p className="text-sm font-medium text-slate-700" data-id="element-25471">
              Astriqo • Premium отчёт
            </p>
            <p className="text-xs text-slate-500 mt-1" data-id="element-25472">
              Отчёт сгенерирован {new Date().toLocaleDateString('ru-RU')} • Все
              права защищены
            </p>
          </div>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-slate-800 transition-colors text-sm font-medium print:hidden" data-id="element-25473">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-25474">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-25475" />
              <polyline points="7 10 12 15 17 10" data-id="element-25476" />
              <line x1="12" y1="15" x2="12" y2="3" data-id="element-25477" />
            </svg>
            Скачать PDF
          </button>
        </div>
      </div>

      {/* CTA BLOCK */}
      <div className="bg-gradient-to-b from-slate-50 to-white py-16 sm:py-24 border-t border-slate-200 relative z-10 print:hidden overflow-hidden" data-id="element-25478">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-25479">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-50/50 rounded-full blur-3xl opacity-50" data-id="element-25480" />
          <div className="absolute top-0 right-0 w-64 h-64 bg-amber-50/50 rounded-full blur-3xl" data-id="element-25481" />
        </div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-25482">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-indigo-100 text-indigo-600 mb-8 shadow-sm border border-indigo-50" data-id="element-25483">
            <Users className="w-8 h-8" data-id="element-25484" />
          </div>

          <h3 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight" data-id="element-25485">
            Хотите персональный разбор от профессионала?
          </h3>

          <p className="text-lg sm:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-25486">
            Закажите индивидуальную консультацию с опытным астрологом для
            глубокого анализа вашей карты и ответов на личные вопросы.
          </p>

          {/* Upsell Ad */}
          <div className="max-w-2xl mx-auto mb-10" data-id="element-25487">
            <AdSlot slotId="SLOT-17" onNavigate={onNavigate} data-id="element-25488" />
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-25489">
            <button onClick={() => onNavigate('astrologers')} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border-2 border-slate-200 bg-white hover:border-indigo-200 hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 text-base font-bold transition-all duration-300" data-id="element-25490">
              Каталог астрологов
            </button>
            <button onClick={() => onNavigate('consultation')} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-base font-bold transition-all duration-300 shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-300 hover:-translate-y-0.5" data-id="element-25491">
              Заказать консультацию
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-1" data-id="element-25492">
                <line x1="5" y1="12" x2="19" y2="12" data-id="element-25493" />
                <polyline points="12 5 19 12 12 19" data-id="element-25494" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>;
}